import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, CheckCircle2, Clock, Zap, XCircle, User,
  Phone, MapPin, Loader2, ArrowRight,
  Inbox, AlertTriangle, Star, Activity,
  Gauge, ClipboardList, ChevronDown, ChevronRight,
  Share2, Copy, CheckCheck, MessageSquare, UserCheck,
  Camera, ImagePlus, Trash2, Upload, X, RefreshCw,
  Pencil, Save, History,
  Package, Wifi,
  CheckSquare, Square, Ban, ThumbsUp, Eye, Mail,
  Hash, Server, Network, CreditCard, Building2, CalendarDays, Layers, Radio, Key,
  Users, Settings
} from 'lucide-react'
import { taskService, type TaskComment } from '@/services/firebase/tasks'
import { storageService } from '@/services/firebase/storage'
import { engineerService, type FieldEngineer } from '@/services/firebase/engineers'
import { customersService } from '@/services/firebase/customers'
import { notificationService } from '@/services/firebase/notifications'
import { useAuthStore } from '@/store/authStore'
import type { Task, TaskStatus, TaskType, BsnlCustomerInput, TaskTimelineEvent } from '@/types'
import BuildingPicker from '@/components/BuildingPicker'
import toast from 'react-hot-toast'

// ── Config ────────────────────────────────────────────────────────────────────
const TYPE_MAP: Record<TaskType, { label: string; icon: string; grad: string; dot: string }> = {
  new_connection: { label: 'New Connection', icon: '🔌', grad: 'linear-gradient(135deg,#2563eb,#06b6d4)', dot: '#2563eb' },
  support:        { label: 'Support',        icon: '🛠️', grad: 'linear-gradient(135deg,#f97316,#ef4444)', dot: '#f97316' },
  enquiry:        { label: 'Enquiry',        icon: '💬', grad: 'linear-gradient(135deg,#8b5cf6,#6366f1)', dot: '#8b5cf6' },
  others:         { label: 'Others',         icon: '📋', grad: 'linear-gradient(135deg,#64748b,#475569)', dot: '#64748b' },
}

const STATUS_CFG: Record<TaskStatus, { label: string; bg: string; text: string; border: string; icon: React.ReactNode; dot: string }> = {
  open:             { label: 'Open',             bg: 'bg-sky-50',     text: 'text-sky-700',     border: 'border-sky-200',     icon: <Inbox size={11} />,        dot: '#0ea5e9' },
  assigned:         { label: 'Assigned',         bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    icon: <User size={11} />,         dot: '#3b82f6' },
  in_progress:      { label: 'In Progress',      bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200',  icon: <Zap size={11} />,          dot: '#8b5cf6' },
  site_visit:       { label: 'Site Visit',       bg: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200',  icon: <MapPin size={11} />,       dot: '#6366f1' },
  work_in_progress: { label: 'Work In Progress', bg: 'bg-orange-50',  text: 'text-orange-700',  border: 'border-orange-200',  icon: <Zap size={11} />,          dot: '#f97316' },
  pending_approval: { label: 'Pending Approval', bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   icon: <Clock size={11} />,        dot: '#f59e0b' },
  pending_feedback: { label: 'Pending Feedback', bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200',  icon: <Star size={11} />,         dot: '#a855f7' },
  closed:           { label: 'Closed',           bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: <CheckCircle2 size={11} />, dot: '#10b981' },
  cancelled:        { label: 'Cancelled',        bg: 'bg-gray-50',    text: 'text-gray-500',    border: 'border-gray-200',    icon: <XCircle size={11} />,      dot: '#9ca3af' },
}

const PRIORITY_CFG: Record<string, { label: string; bar: string; badge: string; badgeText: string; ring: string }> = {
  low:    { label: 'Low',    bar: '#94a3b8', badge: 'bg-gray-100',   badgeText: 'text-gray-600',   ring: 'ring-gray-300'   },
  medium: { label: 'Medium', bar: '#3b82f6', badge: 'bg-blue-100',   badgeText: 'text-blue-700',   ring: 'ring-blue-300'   },
  high:   { label: 'High',   bar: '#f97316', badge: 'bg-orange-100', badgeText: 'text-orange-700', ring: 'ring-orange-300' },
  urgent: { label: 'Urgent', bar: '#ef4444', badge: 'bg-red-100',    badgeText: 'text-red-700',    ring: 'ring-red-300'    },
}

const STATUS_FLOW: Partial<Record<TaskStatus, TaskStatus>> = {
  open:             'assigned',
  assigned:         'in_progress',
  in_progress:      'site_visit',
  site_visit:       'work_in_progress',
  work_in_progress: 'pending_approval',
  pending_approval: 'pending_feedback',
  pending_feedback: 'closed',
}

const DONE_STATUSES: TaskStatus[] = ['closed', 'cancelled']

// Enquiry tasks use workflow phase for status badge instead of stale task.status
const getEnquiryStatusBadge = (task: Task): { label: string; bg: string; text: string; border: string; icon: React.ReactNode; dot: string } | null => {
  if (task.type !== 'enquiry') return null
  const isComplete = task.installationPhase === 'completed' || task.status === 'closed'
  const isInstallation = !!task.installationPhase && task.installationPhase !== 'pending'
  const isProvisioned = task.provisioningStage === 'provisioned'
  const isProvisioning = !!task.provisioningStage && task.provisioningStage !== 'pending'
  if (isComplete)
    return { label: 'Completed', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: <CheckCircle2 size={11} />, dot: '#10b981' }
  if (isInstallation)
    return { label: 'Installing', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: <Zap size={11} />, dot: '#15803d' }
  if (isProvisioned || isProvisioning)
    return { label: 'In Progress', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', icon: <Zap size={11} />, dot: '#8b5cf6' }
  if (task.feasibilityApproved)
    return { label: 'Feasibility Approved', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: <CheckCircle2 size={11} />, dot: '#10b981' }
  if (task.feasibilityStatus)
    return { label: 'Feasibility Review', bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', icon: <Zap size={11} />, dot: '#6366f1' }
  return null
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const Section = ({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50 bg-gray-50/50">
      <Icon size={13} className="text-gray-400" />
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{title}</p>
    </div>
    <div className="p-4">{children}</div>
  </div>
)

const MetaRow = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex items-start justify-between gap-3 py-2.5 border-b border-gray-50 last:border-0">
    <span className="text-[11px] text-gray-400 font-semibold shrink-0">{label}</span>
    <span className={`text-[12px] font-bold text-gray-800 text-right ${mono ? 'font-mono select-all' : ''}`}>{value}</span>
  </div>
)

// ── Small helpers ──────────────────────────────────────────────────────────────
function DetailChip({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <p className="text-[9px] font-black text-slate-400 uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-xs font-bold text-gray-800 truncate">{value}</p>
    </div>
  )
}

// ── Feedback Link Card Component ───────────────────────────────────────────────
function FeedbackLinkCard({ task }: { task: Task }) {
  const [copied, setCopied] = useState(false)
  const feedbackUrl = `${window.location.origin}/feedback/${task.id}`

  const shareViaWhatsApp = () => {
    const message = `Hi ${task.customerName}, your BSNL FTTH installation is complete. Please share your feedback to help us improve our service: ${feedbackUrl}`
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank')
  }

  const copyLink = () => {
    navigator.clipboard.writeText(feedbackUrl)
    setCopied(true)
    toast.success('Feedback link copied!')
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="bg-white rounded-lg border border-purple-200 p-3 space-y-2">
      <p className="text-[10px] font-black text-purple-600 uppercase tracking-wider">Share Feedback Link</p>
      <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
        <input
          type="text"
          value={feedbackUrl}
          readOnly
          className="flex-1 text-xs text-gray-600 bg-transparent outline-none"
        />
        <button
          onClick={copyLink}
          className="text-[10px] font-bold px-2 py-1 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <button
        onClick={shareViaWhatsApp}
        className="w-full py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-2 transition-colors"
      >
        <span>Share via WhatsApp</span>
      </button>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────
const TaskDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'admin'

  const [task, setTask] = useState<Task | null>(null)
  const [loading, setLoading] = useState(true)
  const [engineers, setEngineers] = useState<FieldEngineer[]>([])
  const [engSearch, setEngSearch] = useState('')
  const [showPicker, setShowPicker] = useState(false)
  const [assigning, setAssigning] = useState(false)
  const [showShare, setShowShare] = useState(false)
  const [copied, setCopied] = useState(false)
  const [movingTo, setMovingTo] = useState<TaskStatus | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [approvingId, setApprovingId] = useState<string | null>(null)

  // Verification checklist
  const [tickedFields, setTickedFields] = useState<Record<string, boolean>>({})
  const [mismatchOpen, setMismatchOpen] = useState(false)
  const [mismatchComments, setMismatchComments] = useState('')
  const [reportingMismatch, setReportingMismatch] = useState(false)

  // Site photos
  const [pendingPhotos, setPendingPhotos] = useState<{ file: File; preview: string }[]>([])
  const [uploadProgress, setUploadProgress] = useState<number[]>([])
  const [uploading, setUploading] = useState(false)
  const [savedUrls, setSavedUrls] = useState<string[]>([])

  // Feasibility
  const [feasOpen, setFeasOpen] = useState(false)
  const [feasStatus, setFeasStatus] = useState<'feasible' | 'not_feasible'>('feasible')
  const [feasComments, setFeasComments] = useState('')
  const [feasWorkflows, _setFeasWorkflows] = useState<string[]>([])
  const [feasSubmitting, setFeasSubmitting] = useState(false)
  const [feasLat, setFeasLat] = useState<number | undefined>()
  const [feasLng, setFeasLng] = useState<number | undefined>()
  const [fetchingLoc, setFetchingLoc] = useState(false)
  const [locError, setLocError] = useState<string | null>(null)

  // Feedback
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [rating, setRating] = useState(5)
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(null)
  const [feedbackText, setFeedbackText] = useState('')
  const [catRatings, setCatRatings] = useState({ serviceQuality: 5, responseTime: 5, communication: 5, workmanship: 5 })
  const [submitting, setSubmitting] = useState(false)

  // Comments
  const [comments, setComments] = useState<TaskComment[]>([])
  const [commentText, setCommentText] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)
  const [commentsLoading, setCommentsLoading] = useState(false)

  // Customer details edit (admin only)
  const [editingDetails, setEditingDetails] = useState(false)
  const [editSaving, setEditSaving] = useState(false)
  const [editName, setEditName] = useState('')
  const [editPhone, setEditPhone] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editAddress, setEditAddress] = useState('')
  const [editLocation, setEditLocation] = useState('')
  const [editCity, setEditCity] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editNotes, setEditNotes] = useState('')

  // Provisioning workflow (enquiry type, feasible)
  const [provSaving, setProvSaving] = useState(false)
  const [provPlan, setProvPlan] = useState('')
  const [provPlanPeriod, setProvPlanPeriod] = useState('MONTHLY')
  const [provFmc, setProvFmc] = useState('')
  const [provCustomerName, setProvCustomerName] = useState('')
  const [provCustomerPhone, setProvCustomerPhone] = useState('')
  const [provCustomerEmail, setProvCustomerEmail] = useState('')
  const [provCustomerAddress, setProvCustomerAddress] = useState('')
  const [provCustomerCity, setProvCustomerCity] = useState('')
  const [provCustomerLocation, setProvCustomerLocation] = useState('')
  const [provCustomerFormOpen, setProvCustomerFormOpen] = useState(false)
  // Extended customer onboarding fields (matching BsnlCustomer)
  const [provCategory, setProvCategory] = useState('Individual')
  const [provExchangeCode, setProvExchangeCode] = useState('')
  const [provServiceNumber, setProvServiceNumber] = useState('')
  const [provSubServiceType, setProvSubServiceType] = useState('BHARAT FIBER COMBO')
  const [provBillingAccountNo, setProvBillingAccountNo] = useState('')
  const [provOltIp, setProvOltIp] = useState('')
  const [provBbUserId, setProvBbUserId] = useState('')
  const [provVoipPassword, setProvVoipPassword] = useState('')
  const [provVlan, setProvVlan] = useState('')
  const [provPort, setProvPort] = useState('')
  const [provBuilding, setProvBuilding] = useState('')
  const [provActivationDate, setProvActivationDate] = useState('')

  // Provisioning plans loaded from customer list
  const [availablePlans, setAvailablePlans] = useState<{ name: string; fmc: number; period: string }[]>([])
  const [useCustomPlan, setUseCustomPlan] = useState(false)

  // Detail tabs (Description / Comments / History)
  const [detailTab, setDetailTab] = useState<'details' | 'comments' | 'history'>('details')
  const [checklistExpanded, setChecklistExpanded] = useState(false)

  // Accordion state for installation verification
  const [openInstSections, setOpenInstSections] = useState<Record<string, boolean>>({
    customer: true,
    connection: true,
    plan: false,
    technical: false
  })

  // Workflow tab selector (enquiry/feasibility/provisioning/installation)
  const [workflowTab, setWorkflowTab] = useState<'enquiry' | 'feasibility' | 'provisioning' | 'installation'>('enquiry')

  // Enquiry completion decision
  const [enquiryCloseComment, setEnquiryCloseComment] = useState('')
  const [closingEnquiry, setClosingEnquiry] = useState(false)
  const [markingComplete, setMarkingComplete] = useState(false)
  const [showCloseNoProvForm, setShowCloseNoProvForm] = useState(false)

  // Installation sub-workflow engineer picker
  const [instEngSearch, setInstEngSearch] = useState('')
  const [showInstEngPicker, setShowInstEngPicker] = useState(false)
  const [assigningInstEng, setAssigningInstEng] = useState(false)
  const [instPhaseSaving, setInstPhaseSaving] = useState(false)
  const [instVerifSaving, setInstVerifSaving] = useState(false)

  // Speed test states
  const [testDlSpeed, setTestDlSpeed] = useState<number | null>(task?.speedTestDownload || null)
  const [testUlSpeed, setTestUlSpeed] = useState<number | null>(task?.speedTestUpload || null)
  const [testPing, setTestPing] = useState<number | null>(task?.speedTestPing || null)
  const [testIsp, setTestIsp] = useState<string | null>(task?.speedTestIsp || null)
  const [testIp, setTestIp] = useState<string | null>(task?.speedTestIp || null)
  const [testLat, setTestLat] = useState<number | null>(task?.speedTestLatitude || null)
  const [testLng, setTestLng] = useState<number | null>(task?.speedTestLongitude || null)
  const [testingState, setTestingState] = useState<'idle' | 'checking_isp' | 'ping' | 'download' | 'upload' | 'saving' | 'done'>('idle')
  const [speedTestProgress, setSpeedTestProgress] = useState(0)
  const [speedTestError, setSpeedTestError] = useState<string | null>(null)
  const [ispOverride, setIspOverride] = useState(false)

  // Sync speed test state when task data loads (for viewing saved results)
  useEffect(() => {
    if (task?.speedTestDownload !== undefined) setTestDlSpeed(task.speedTestDownload)
    if (task?.speedTestUpload !== undefined) setTestUlSpeed(task.speedTestUpload)
    if (task?.speedTestPing !== undefined) setTestPing(task.speedTestPing)
    if (task?.speedTestIsp !== undefined) setTestIsp(task.speedTestIsp)
    if (task?.speedTestIp !== undefined) setTestIp(task.speedTestIp)
    if (task?.speedTestLatitude !== undefined) setTestLat(task.speedTestLatitude)
    if (task?.speedTestLongitude !== undefined) setTestLng(task.speedTestLongitude)
  }, [task?.speedTestDownload, task?.speedTestUpload, task?.speedTestPing, task?.speedTestIsp, task?.speedTestIp, task?.speedTestLatitude, task?.speedTestLongitude])

  const engineerId = user?.uid ?? user?.id ?? ''

  const loadComments = useCallback(async () => {
    if (!id) return
    setCommentsLoading(true)
    const res = await taskService.listComments(id)
    if (res.success && res.data) setComments(res.data)
    setCommentsLoading(false)
  }, [id])

  // Timeline
  const [timelineEvents, setTimelineEvents] = useState<TaskTimelineEvent[]>([])
  const [timelineLoading, setTimelineLoading] = useState(false)

  const loadTimeline = useCallback(async () => {
    if (!id) return
    setTimelineLoading(true)
    const res = await taskService.listTimelineEvents(id)
    if (res.success && res.data) setTimelineEvents(res.data)
    setTimelineLoading(false)
  }, [id])

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    const res = await taskService.get(id!)
    if (res.success && res.data) {
      const t = res.data
      // Engineers can only view tasks assigned to them or open (claimable) tasks
      if (!isAdmin) {
        const isAssignedToMe = t.assignedTo === engineerId || t.assignedTo === user?.engineerDocId
        const isOpen = t.status === 'open'
        if (!isAssignedToMe && !isOpen) {
          toast.error('You do not have access to this task')
          navigate(-1)
          return
        }
      }
      setTask(t)
      setTickedFields(t.verificationTicked || {})
      setSavedUrls(t.sitePhotos ?? [])
      setMismatchComments(t.mismatchComments || '')
    } else {
      toast.error('Task not found')
      navigate(-1)
    }
    setLoading(false)
  }, [id, navigate, isAdmin, engineerId, user?.engineerDocId])

  useEffect(() => { load() }, [load])
  useEffect(() => { loadComments() }, [loadComments])
  useEffect(() => { loadTimeline() }, [loadTimeline])

  // Auto-select workflow tab based on current phase
  useEffect(() => {
    if (!task || task.type !== 'enquiry') return

    // Determine current phase and set workflow tab
    if (task.installationPhase && task.installationPhase !== 'pending') {
      setWorkflowTab('installation')
    } else if (task.provisioningStage === 'provisioned') {
      setWorkflowTab('installation')  // Provisioning done → ready to install
    } else if (task.provisioningStage && task.provisioningStage !== 'pending') {
      setWorkflowTab('provisioning')
    } else if (task.feasibilityStatus) {
      setWorkflowTab('feasibility')
    } else {
      setWorkflowTab('enquiry')
    }
  }, [task])

  useEffect(() => {
    engineerService.list().then(res => {
      if (res.success && res.data) setEngineers(res.data.filter(e => e.isActive && e.role === 'engineer'))
    })
  }, [])

  useEffect(() => {
    customersService.list().then(res => {
      if (res.success && res.data) {
        // Extract unique plans from customer records
        const seen = new Set<string>()
        const plans: { name: string; fmc: number; period: string }[] = []
        for (const c of res.data) {
          const key = `${c.subscriptionPlan}|${c.fmc}`
          if (c.subscriptionPlan && !seen.has(key)) {
            seen.add(key)
            plans.push({ name: c.subscriptionPlan, fmc: c.fmc, period: c.planPeriod || 'MONTHLY' })
          }
        }
        plans.sort((a, b) => a.fmc - b.fmc)
        setAvailablePlans(plans)
      }
    })
  }, [])

  const fetchLocation = useCallback(() => {
    if (!navigator.geolocation) { setLocError('Geolocation not supported'); return }
    setFetchingLoc(true); setLocError(null)
    navigator.geolocation.getCurrentPosition(
      pos => { setFeasLat(pos.coords.latitude); setFeasLng(pos.coords.longitude); setFetchingLoc(false) },
      err => {
        const msg = err.code === 1 ? 'Permission denied' : err.code === 2 ? 'Location unavailable' : 'Timeout'
        setLocError(msg); setFetchingLoc(false)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    )
  }, [])

  useEffect(() => { if (feasOpen) fetchLocation() }, [feasOpen, fetchLocation])

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
      <div className="w-14 h-14 rounded-3xl flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg,rgba(37,99,235,0.1),rgba(6,182,212,0.1))' }}>
        <Loader2 size={24} className="text-blue-500 animate-spin" />
      </div>
      <p className="text-sm text-gray-400 font-medium">Loading task…</p>
    </div>
  )

  if (!task) return null

  const t = TYPE_MAP[task.type]
  const s = getEnquiryStatusBadge(task) ?? STATUS_CFG[task.status]
  const p = PRIORITY_CFG[task.priority] ?? PRIORITY_CFG.medium
  const displayType = task.type === 'others' && task.customType ? task.customType : t.label
  const initials = task.customerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  const nextStatus = STATUS_FLOW[task.status]
  const isDone = DONE_STATUSES.includes(task.status)
  const isSiteVisit = task.status === 'site_visit'
  const isEnquirySiteVisit = task.type === 'enquiry' && task.status === 'site_visit'
  const isEnquiryInProgress = task.type === 'enquiry' && task.status === 'in_progress'
  const canAssign = isAdmin && !isDone
  const filteredEngs = engineers.filter(e =>
    !engSearch || e.name.toLowerCase().includes(engSearch.toLowerCase()) || e.location.toLowerCase().includes(engSearch.toLowerCase())
  )

  const fieldsToVerify = [
    { key: 'customerName', label: 'Customer Name', value: task.customerName },
    { key: 'customerPhone', label: 'Mobile Number', value: `+91 ${task.customerPhone}` },
    { key: 'address', label: 'Address', value: task.address },
    ...(task.location ? [{ key: 'location', label: 'Building', value: task.location }] : []),
    { key: 'city', label: 'City', value: task.city },
    ...(task.serviceNumber ? [{ key: 'serviceNumber', label: 'Service No.', value: task.serviceNumber }] : []),
    ...(task.bbUserId ? [{ key: 'bbUserId', label: 'BB User ID', value: task.bbUserId }] : []),
    ...(task.oltIp ? [{ key: 'oltIp', label: 'OLT IP', value: task.oltIp }] : []),
  ]

  const shareMessage = `*BSNL FTTH Task — ${task.taskNumber}*\n\n👤 ${task.customerName}\n📞 +91 ${task.customerPhone}\n📍 ${task.address}${task.city ? ', ' + task.city : ''}\n\n🏷️ Type: ${displayType}\n⚡ Status: ${s.label}\n🔴 Priority: ${p.label}`

  const handleAssign = async (eng: FieldEngineer) => {
    setAssigning(true)
    const res = await taskService.assignEngineer(task.id, eng.id || eng.uid || '', eng.name)
    if (res.success) { toast.success(`Assigned to ${eng.name}`); setShowPicker(false); load(); loadTimeline() }
    else toast.error(res.error ?? 'Assignment failed')
    setAssigning(false)
  }

  const handleStatusChange = async (status: TaskStatus) => {
    setMovingTo(status)
    const res = await taskService.updateStatus(task.id, status, user?.name || user?.role || 'System')
    if (res.success) { toast.success(`Moved to ${STATUS_CFG[status].label}`); load(); loadTimeline() }
    else toast.error(res.error ?? 'Failed to update status')
    setMovingTo(null)
  }

  const handleCancel = async () => {
    setCancelling(true)
    const res = await taskService.updateStatus(task.id, 'cancelled', user?.name || user?.role || 'System')
    if (res.success) { toast.success('Task cancelled'); navigate(-1) }
    else toast.error(res.error ?? 'Failed to cancel')
    setCancelling(false)
  }

  const handleApprove = async () => {
    setApprovingId(task.id)
    try {
      const r = await taskService.approveTask(task.id, user?.uid ?? user?.id ?? 'admin', user?.name)
      if (!r.success) { toast.error(r.error ?? 'Approval failed'); return }
      if (task.type === 'enquiry') {
        try {
          const phoneDigits = task.customerPhone.replace(/\D/g, '').padStart(10, '0')
          const areaCode = task.city.toLowerCase().includes('kozhikode') ? '0495' : '0471'
          const serviceNumber = `${areaCode}-${phoneDigits.slice(-7)}`
          const customerInput: BsnlCustomerInput = {
            category: 'Individual', exchangeCode: 'TVMTPK', serviceNumber,
            subServiceType: 'BHARAT FIBER COMBO', subscriptionPlan: 'FIBRE BASIC-FBB-COMBO',
            planPeriod: 'MONTHLY', customerName: task.customerName,
            billingAccountNo: Number('912' + phoneDigits.slice(-7)),
            mobile: Number(phoneDigits), email: task.customerEmail || null,
            address: task.address, fmc: 499, oltIp: '10.215.181.225',
            bbUserId: `ap${areaCode}${phoneDigits.slice(-7)}_sid`, voipPassword: null,
            vlan: 400 + (Number(phoneDigits.slice(-3)) % 500),
            port: (Number(phoneDigits.slice(-1)) % 8) + 1,
            building: task.location || '',
            activationDate: (() => {
              const now = new Date()
              return `${String(now.getDate()).padStart(2,'0')}/${String(now.getMonth()+1).padStart(2,'0')}/${now.getFullYear()} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`
            })()
          }
          const custRes = await customersService.create(customerInput)
          if (custRes.success) toast.success(`Customer profile created for ${task.customerName}!`)
        } catch { toast.error('Failed to auto-create customer profile') }
      }
      if (task.type === 'enquiry') {
        toast.success('Enquiry approved and successfully closed')
      } else {
        toast.success('Task approved — awaiting customer feedback')
      }
      load()
      loadTimeline()
    } catch { toast.error('Approval execution failed') }
    setApprovingId(null)
  }

  const handleToggleTick = async (key: string) => {
    const next = { ...tickedFields, [key]: !tickedFields[key] }
    setTickedFields(next)
    await taskService.updateVerificationTicks(task.id, next)
  }

  const handleReportMismatch = async () => {
    if (!mismatchComments.trim()) { toast.error('Enter mismatch details'); return }
    setReportingMismatch(true)
    const res = await taskService.reportMismatch(task.id, mismatchComments.trim())
    setReportingMismatch(false)
    if (res.success) { toast.success('Mismatch reported'); setMismatchOpen(false); load(); loadTimeline() }
    else toast.error(res.error ?? 'Failed to report')
  }

  const handlePickPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    setPendingPhotos(prev => [...prev, ...files.map(f => ({ file: f, preview: URL.createObjectURL(f) }))])
    e.target.value = ''
  }

  const handleUploadPhotos = async () => {
    if (!pendingPhotos.length) return
    setUploading(true)
    setUploadProgress(pendingPhotos.map(() => 0))
    const urls = [...savedUrls]
    for (let i = 0; i < pendingPhotos.length; i++) {
      const { file } = pendingPhotos[i]
      const compressed = await storageService.compressImage(file, 1920, 1080, 0.8)
      const path = storageService.generateFilePath(`tasks/${task.id}/site_photos`, file.name)
      const res = await storageService.uploadFile(path, compressed, p => {
        setUploadProgress(prev => { const n = [...prev]; n[i] = p; return n })
      })
      if (res.success && res.data) urls.push(res.data)
      else { toast.error(`Failed to upload ${file.name}`); setUploading(false); return }
    }
    const saveRes = await taskService.attachSitePhotos(task.id, urls)
    if (saveRes.success) { setSavedUrls(urls); setPendingPhotos([]); setUploadProgress([]); toast.success(`${pendingPhotos.length} photo(s) uploaded`) }
    else toast.error(saveRes.error ?? 'Could not save photos')
    setUploading(false)
  }

  const handleFeasibilitySubmit = async () => {
    if (feasLat === undefined || feasLng === undefined) { toast.error('GPS coordinates required'); return }
    setFeasSubmitting(true)
    const res = await taskService.submitFeasibilityStudy(task.id, {
      feasibilityStatus: feasStatus, feasibilityComments: feasComments,
      feasibilityWorkflowRequired: feasWorkflows, latitude: feasLat, longitude: feasLng
    }, user?.name || 'Engineer')
    if (res.success) {
      // Auto-advance to work_in_progress so admin can review
      await taskService.updateStatus(task.id, 'work_in_progress', user?.name || 'Engineer')
      toast.success('Feasibility study submitted — awaiting admin review')
      setFeasOpen(false)
      load()
      loadTimeline()
    } else toast.error(res.error ?? 'Submission failed')
    setFeasSubmitting(false)
  }

  const handleFeedbackSubmit = async () => {
    if (wouldRecommend === null) { toast.error('Select a recommendation option'); return }
    setSubmitting(true)
    const res = await taskService.submitFeedback(task.id, { rating, feedback: feedbackText, wouldRecommend, categories: catRatings }, user?.name || 'Customer')
    if (res.success) { toast.success('Feedback saved — ticket closed! 🎉'); load(); loadTimeline() }
    else toast.error(res.error ?? 'Submit failed')
    setSubmitting(false)
  }

  const handleStartEdit = () => {
    if (!task) return
    setEditName(task.customerName)
    setEditPhone(task.customerPhone)
    setEditEmail(task.customerEmail || '')
    setEditAddress(task.address)
    setEditLocation(task.location || '')
    setEditCity(task.city)
    setEditDescription(task.description || '')
    setEditNotes(task.notes || '')
    setEditingDetails(true)
  }

  const handleSaveDetails = async () => {
    if (!editName.trim()) { toast.error('Customer name is required'); return }
    if (!editPhone.trim()) { toast.error('Phone number is required'); return }
    if (!editAddress.trim()) { toast.error('Address is required'); return }
    if (!editCity.trim()) { toast.error('City is required'); return }
    setEditSaving(true)
    const res = await taskService.update(task!.id, {
      customerName: editName.trim(),
      customerPhone: editPhone.trim(),
      customerEmail: editEmail.trim() || undefined,
      address: editAddress.trim(),
      location: editLocation.trim() || undefined,
      city: editCity.trim(),
      description: editDescription.trim() || undefined,
      notes: editNotes.trim() || undefined,
    })
    if (res.success) { toast.success('Task details updated'); setEditingDetails(false); load() }
    else toast.error(res.error ?? 'Failed to save')
    setEditSaving(false)
  }

  const handleSubmitComment = async () => {
    if (!commentText.trim()) return
    setSubmittingComment(true)
    const taggedAdmin = commentText.toLowerCase().includes('@admin')
    const res = await taskService.addComment(task!.id, {
      authorId: engineerId,
      authorName: user?.name ?? 'Engineer',
      authorRole: isAdmin ? 'admin' : 'engineer',
      text: commentText.trim(),
      taggedAdmin,
    })
    if (res.success) {
      setCommentText('')
      loadComments()
      if (taggedAdmin && !isAdmin) {
        notificationService.sendCommentNotification({
          engineerName: user?.name ?? 'Engineer',
          taskId: task!.id,
          taskNumber: task!.taskNumber,
          commentText: commentText.trim(),
        })
      }
    } else {
      toast.error(res.error ?? 'Failed to post comment')
    }
    setSubmittingComment(false)
  }

  // ── Enquiry completion handlers ──────────────────────────────────────────
  const handleMarkEnquiryComplete = async () => {
    setMarkingComplete(true)
    const res = await taskService.updateStatus(task!.id, 'pending_approval', user?.name || 'Admin')
    if (res.success) { toast.success('Enquiry marked complete — choose next step'); load(); loadTimeline() }
    else toast.error(res.error ?? 'Failed')
    setMarkingComplete(false)
  }

  const handleCloseEnquiryNoProvisioning = async () => {
    if (!enquiryCloseComment.trim()) { toast.error('Please add a comment explaining why provisioning is not required'); return }
    setClosingEnquiry(true)
    await taskService.addComment(task!.id, {
      authorId: user?.uid ?? user?.id ?? 'admin',
      authorName: user?.name ?? 'Admin',
      authorRole: 'admin',
      text: `Closed without provisioning: ${enquiryCloseComment.trim()}`,
      taggedAdmin: false,
    })
    const res = await taskService.approveTask(task!.id, user?.uid ?? user?.id ?? 'admin', user?.name)
    if (res.success) { toast.success('Docket closed — no provisioning required'); load(); loadTimeline() }
    else toast.error(res.error ?? 'Failed to close')
    setClosingEnquiry(false)
  }

  // ── Provisioning handlers ────────────────────────────────────────────────
  const handleProvStage = async (stage: string, extra?: Record<string, unknown>) => {
    setProvSaving(true)
    const res = await taskService.updateProvisioningStage(task!.id, stage, extra)
    if (res.success) { toast.success('Updated'); load(); loadTimeline() }
    else toast.error(res.error ?? 'Failed')
    setProvSaving(false)
  }

  const handleCustomerProvisioned = async () => {
    if (!provCustomerName.trim() || !provCustomerPhone.trim() || !provServiceNumber.trim()) {
      toast.error('Name, phone and service number are required'); return
    }
    setProvSaving(true)
    const now = new Date()
    const activationDate = provActivationDate || `${String(now.getDate()).padStart(2,'0')}/${String(now.getMonth()+1).padStart(2,'0')}/${now.getFullYear()} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`

    const customerInput: BsnlCustomerInput = {
      category: provCategory.trim() || 'Individual',
      exchangeCode: provExchangeCode.trim() || 'TVMTPK',
      serviceNumber: provServiceNumber.trim(),
      subServiceType: provSubServiceType.trim() || 'BHARAT FIBER COMBO',
      subscriptionPlan: provPlan.trim() || 'FIBRE BASIC-FBB-COMBO',
      planPeriod: provPlanPeriod || 'MONTHLY',
      customerName: provCustomerName.trim(),
      billingAccountNo: Number(provBillingAccountNo) || 0,
      mobile: Number(provCustomerPhone.replace(/\D/g, '')),
      email: provCustomerEmail.trim() || null,
      address: provCustomerAddress.trim(),
      fmc: Number(provFmc) || 499,
      oltIp: provOltIp.trim() || '10.215.181.225',
      bbUserId: provBbUserId.trim() || `ap${provExchangeCode || 'TVMTPK'}${provCustomerPhone.replace(/\D/g, '').slice(-7)}_sid`,
      voipPassword: provVoipPassword.trim() || null,
      vlan: provVlan ? Number(provVlan) : null,
      port: provPort ? Number(provPort) : null,
      building: provBuilding.trim() || provCustomerLocation.trim() || '',
      activationDate,
    }

    // Create customer in customer list first
    const custRes = await customersService.create(customerInput)
    if (!custRes.success) {
      toast.error(custRes.error ?? 'Failed to add customer to list')
      setProvSaving(false)
      return
    }

    // Build updates and filter out undefined values (Firestore rejects undefined)
    const currentChecklist = task!.provisioningChecklist ?? []
    const updatedChecklist = currentChecklist.includes('details_obtained')
      ? currentChecklist
      : [...currentChecklist, 'details_obtained']

    const updates: Record<string, unknown> = {
      provisionedName: provCustomerName.trim(),
      provisionedPhone: provCustomerPhone.trim(),
      provisionedAt: now.toISOString(),
      serviceNumber: provServiceNumber.trim(),
      bbUserId: provBbUserId.trim() || customerInput.bbUserId,
      oltIp: provOltIp.trim() || customerInput.oltIp,
      vlan: customerInput.vlan,
      port: customerInput.port,
      voipPassword: customerInput.voipPassword,
      exchangeCode: provExchangeCode.trim() || customerInput.exchangeCode,
      subServiceType: provSubServiceType.trim() || customerInput.subServiceType,
      category: provCategory.trim() || customerInput.category,
      billingAccountNo: customerInput.billingAccountNo,
      fmc: customerInput.fmc,
      subscriptionPlan: customerInput.subscriptionPlan,
      planPeriod: customerInput.planPeriod,
      provisioningPlan: customerInput.subscriptionPlan,
      provisioningPlanPeriod: customerInput.planPeriod,
      provisioningFmc: customerInput.fmc,
      activationDate,
      installationPhase: 'pending',
      provisioningChecklist: updatedChecklist,
    }
    if (provCustomerEmail.trim()) updates.provisionedEmail = provCustomerEmail.trim()
    if (provCustomerAddress.trim()) updates.provisionedAddress = provCustomerAddress.trim()
    if (provCustomerCity.trim()) updates.provisionedCity = provCustomerCity.trim()
    if (provCustomerLocation.trim()) updates.provisionedLocation = provCustomerLocation.trim()

    const res = await taskService.updateProvisioningStage(task!.id, 'provisioned', updates)
    if (res.success) {
      toast.success('Customer provisioned and added to customer list!')
      setProvCustomerFormOpen(false)
      load()
    } else toast.error(res.error ?? 'Failed')
    setProvSaving(false)
  }

  // ── Installation phase handlers ──────────────────────────────────────────
  const handleAssignInstEng = async (eng: FieldEngineer) => {
    setAssigningInstEng(true)
    const res = await taskService.assignInstallationEngineer(task!.id, eng.id || eng.uid || '', eng.name)
    if (res.success) { toast.success(`Installation assigned to ${eng.name}`); setShowInstEngPicker(false); load() }
    else toast.error(res.error ?? 'Assignment failed')
    setAssigningInstEng(false)
  }

  const handleInstPhase = async (phase: string) => {
    setInstPhaseSaving(true)
    const res = await taskService.updateInstallationPhase(task!.id, phase)
    if (res.success) { toast.success('Installation phase updated'); load() }
    else toast.error(res.error ?? 'Failed')
    setInstPhaseSaving(false)
  }

  const handleLoadInstData = async () => {
    setInstVerifSaving(true)
    const res = await taskService.updateInstallationVerification(task!.id, { dataLoaded: true })
    if (res.success) { toast.success('Customer data loaded'); load() }
    else toast.error(res.error ?? 'Failed')
    setInstVerifSaving(false)
  }

  const handleToggleInstVerif = async (key: string) => {
    const current = task!.installationVerification ?? {}
    const next = { ...current, [key]: !current[key] }
    setInstVerifSaving(true)
    const res = await taskService.updateInstallationVerification(task!.id, { verification: next })
    if (res.success) { load() }
    else toast.error(res.error ?? 'Failed')
    setInstVerifSaving(false)
  }

  // Speed test function
  const runSpeedTest = async () => {
    setTestingState('checking_isp')
    setSpeedTestError(null)
    setSpeedTestProgress(0)

    try {
      let ip = '0.0.0.0'
      let isp = 'Unknown'

      // Get IP and ISP info
      try {
        const ipRes = await fetch('https://speed.cloudflare.com/meta', { cache: 'no-store' })
        const ipData = await ipRes.json()
        ip = ipData?.clientIp ?? '0.0.0.0'
        isp = ipData?.asOrganization ?? 'Unknown'
        setTestIp(ip)
        setTestIsp(isp)
      } catch {
        // Continue without IP info
      }

      // Check if connected to BSNL
      const isBsnl = isp.toLowerCase().includes('bsnl') ||
                     isp.toLowerCase().includes('bharat') ||
                     isp.toLowerCase().includes('telecom')

      if (!isBsnl && !ispOverride) {
        setSpeedTestError(`⚠️ Network Check Failed: Connected to ${isp}. Please connect to the customer's BSNL modem's Wi-Fi network.`)
        setTestingState('idle')
        return
      }

      setSpeedTestProgress(10)

      // Latency/Ping
      setTestingState('ping')
      let totalRtt = 0
      const pingIterations = 3
      for (let i = 0; i < pingIterations; i++) {
        const start = performance.now()
        await fetch('https://speed.cloudflare.com/__down?bytes=0', { cache: 'no-store' })
        const end = performance.now()
        totalRtt += (end - start)
        setSpeedTestProgress(10 + Math.round(((i + 1) / pingIterations) * 20))
      }
      const ping = Math.round(totalRtt / pingIterations)
      setTestPing(ping)

      // Download Speed Test
      setTestingState('download')
      const dlStart = performance.now()
      const dlRes = await fetch('https://speed.cloudflare.com/__down?bytes=2000000', { cache: 'no-store' })
      const dlBlob = await dlRes.blob()
      const dlEnd = performance.now()
      const dlDuration = (dlEnd - dlStart) / 1000
      const dlBits = dlBlob.size * 8
      const dlSpeed = Number(((dlBits / dlDuration) / (1024 * 1024)).toFixed(2))
      setTestDlSpeed(dlSpeed)
      setSpeedTestProgress(60)

      // Upload Speed Test
      setTestingState('upload')
      const ulStart = performance.now()
      const ulData = new Uint8Array(1000000)
      await fetch('https://speed.cloudflare.com/__up', {
        method: 'POST',
        body: ulData,
        cache: 'no-store'
      })
      const ulEnd = performance.now()
      const ulDuration = (ulEnd - ulStart) / 1000
      const ulBits = ulData.length * 8
      const ulSpeed = Number(((ulBits / ulDuration) / (1024 * 1024)).toFixed(2))
      setTestUlSpeed(ulSpeed)
      setSpeedTestProgress(85)

      // Capture GPS Location
      setTestingState('saving')
      let latitude: number | undefined
      let longitude: number | undefined
      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 })
        })
        latitude = position.coords.latitude
        longitude = position.coords.longitude
        setTestLat(latitude)
        setTestLng(longitude)
      } catch {
        // GPS capture failed - continue without location
      }

      // Save Results
      const recordRes = await taskService.recordSpeedTest(task!.id, {
        download: dlSpeed,
        upload: ulSpeed,
        ping: ping,
        isp: isp,
        ip: ip,
        latitude,
        longitude
      })

      if (recordRes.success) {
        setSpeedTestProgress(100)
        setTestingState('done')
        toast.success('Speed test completed and recorded! 🚀')
        load()
      } else {
        throw new Error(recordRes.error || 'Failed to save results')
      }
    } catch (err) {
      setSpeedTestError(err instanceof Error ? err.message : 'Network speed test failed. Please try again.')
      setTestingState('idle')
    }
  }

  const handleWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`)
  const handleSMS = () => window.open(`sms:?body=${encodeURIComponent(shareMessage)}`)
  const handleCopy = () => { navigator.clipboard.writeText(shareMessage); setCopied(true); setTimeout(() => setCopied(false), 2000) }

  // ── Derived helpers ──────────────────────────────────────────────────────
  const PROV_STAGES = ['pending','customer_confirmed','plan_selected','bsnl_registered','provisioned'] as const
  type ProvStage = typeof PROV_STAGES[number]
  // Installation phases with new intermediate steps
  const INST_PHASES = ['pending','assigned','in_progress','site_visit','work_in_progress','installation_done','speed_test','pending_approval','completed'] as const
  type InstPhase = typeof INST_PHASES[number]
  const INST_PHASE_LABELS: Record<InstPhase, string> = {
    pending: 'Pending',
    assigned: 'Assigned',
    in_progress: 'In Progress',
    site_visit: 'Site Visit',
    work_in_progress: 'Work in Progress',
    installation_done: 'Installation Completed',
    speed_test: 'Speed Test',
    pending_approval: 'Pending Approval',
    completed: 'Completed',
  }
  const INST_NEXT: Partial<Record<InstPhase, InstPhase>> = {
    pending: 'assigned',
    assigned: 'in_progress',
    in_progress: 'site_visit',
    site_visit: 'work_in_progress',
    work_in_progress: 'installation_done',
    installation_done: 'speed_test',
    speed_test: 'pending_approval',
    pending_approval: 'completed',
  }
  const filteredInstEngs = engineers.filter(e =>
    !instEngSearch || e.name.toLowerCase().includes(instEngSearch.toLowerCase()) || e.location.toLowerCase().includes(instEngSearch.toLowerCase())
  )

  const renderProvisioningSection = () => {
    if (task!.type !== 'enquiry') return null
    if (task!.feasibilityStatus !== 'feasible') return null
    if (!isAdmin) return null

    const stage = (task!.provisioningStage ?? 'pending') as ProvStage
    const checklist = task!.provisioningChecklist ?? []
    const isProvisioned = stage === 'provisioned'

    const PROV_ITEMS = [
      { key: 'contacted', label: 'Contact customer' },
      { key: 'responded', label: 'Customer responded with details' },
      { key: 'plan_selected', label: 'Plan selected by customer' },
      { key: 'shared_bsnl', label: 'Shared to BSNL for provisioning' },
      { key: 'provisioned_bsnl', label: 'Customer provisioned by BSNL' },
      { key: 'details_obtained', label: 'Customer provisioning details obtained' },
    ] as const

    const allChecked = PROV_ITEMS.every(item => checklist.includes(item.key))
    const checkedCount = PROV_ITEMS.filter(item => checklist.includes(item.key)).length

    const toggleChecklistItem = async (key: string) => {
      const next = checklist.includes(key)
        ? checklist.filter(k => k !== key)
        : [...checklist, key]
      setProvSaving(true)
      const res = await taskService.updateProvisioningChecklist(task!.id, next)
      if (res.success) { toast.success('Updated'); load(); loadTimeline() }
      else toast.error(res.error ?? 'Failed')
      setProvSaving(false)
    }

    const handleCompleteProvisioning = async () => {
      setProvSaving(true)
      const res = await taskService.updateProvisioningStage(task!.id, 'provisioned', {
        provisionedAt: new Date().toISOString(),
      })
      if (res.success) { toast.success('Provisioning completed'); load(); loadTimeline() }
      else toast.error(res.error ?? 'Failed')
      setProvSaving(false)
    }

    // ── shared input style ──
    const inp = "w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
    const lbl = "text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-1"
    const iw = "flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400 hover:border-gray-300 bg-white transition-all"

    const isProvStarted = stage !== 'pending'

    return (
      <div className="space-y-3">
        {/* ── START PROVISIONING GATE ── */}
        {!isProvStarted && (
          <div className="p-6 text-center bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3"
              style={{ background: 'linear-gradient(135deg,#2563eb,#6366f1)', boxShadow: '0 4px 14px rgba(37,99,235,0.25)' }}>
              <Package size={28} className="text-white" />
            </div>
            <p className="text-sm font-black text-gray-800 mb-1">Feasibility Approved</p>
            <p className="text-xs text-gray-500 mb-5 max-w-xs mx-auto">Site is feasible. Click below to begin the provisioning workflow.</p>
            <button onClick={() => handleProvStage('customer_confirmed', { provisioningChecklist: [], status: 'in_progress' })} disabled={provSaving}
              className="px-8 py-3 text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 mx-auto disabled:opacity-60 transition-all active:scale-[0.98]"
              style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', boxShadow: '0 4px 14px rgba(37,99,235,0.3)' }}>
              {provSaving ? <Loader2 size={15} className="animate-spin" /> : <ThumbsUp size={15} />}
              Start Provisioning
            </button>
          </div>
        )}

        {/* Header */}
        {isProvStarted && (
          <div className="flex items-center gap-2.5 px-1">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: 'linear-gradient(135deg,#2563eb,#6366f1)' }}>
              <Package size={13} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-gray-800">Provisioning Workflow</p>
              <p className="text-[10px] text-gray-400 font-semibold">
                {checkedCount} of {PROV_ITEMS.length} completed
              </p>
            </div>
            {isProvisioned && (
              <span className="ml-auto text-[10px] font-black px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                ✅ Complete
              </span>
            )}
          </div>
        )}

        {/* Checklist */}
        {isProvStarted && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-blue-50/60 border-b border-blue-100 flex items-center gap-2">
              <ClipboardList size={14} className="text-blue-600" />
              <p className="text-xs font-black text-blue-800">Provisioning Checklist</p>
              <span className="ml-auto text-[10px] font-black px-2 py-0.5 rounded-full bg-blue-100 text-blue-600">
                {checkedCount}/{PROV_ITEMS.length}
              </span>
            </div>
            <div className="divide-y divide-gray-50">
              {PROV_ITEMS.map((item, idx) => {
                const checked = checklist.includes(item.key)
                const isDetailsItem = item.key === 'details_obtained'
                const hasDetails = !!task!.provisionedName
                return (
                  <div key={item.key} className={`flex items-start gap-3 px-4 py-3 transition-colors ${checked ? 'bg-emerald-50/30' : ''}`}>
                    <button
                      onClick={() => {
                        if (isDetailsItem && !hasDetails) {
                          // Pre-fill only customer contact details; service & plan details must be entered manually
                          setProvCustomerName(task!.customerName)
                          setProvCustomerPhone(task!.customerPhone)
                          setProvCustomerEmail(task!.customerEmail || '')
                          setProvCustomerAddress(task!.address)
                          setProvCustomerCity(task!.city)
                          setProvCustomerLocation(task!.location || '')
                          setProvBuilding(task!.location || '')
                          setProvCategory('')
                          setProvExchangeCode('')
                          setProvServiceNumber('')
                          setProvSubServiceType('')
                          setProvBillingAccountNo('')
                          setProvOltIp('')
                          setProvBbUserId('')
                          setProvVoipPassword('')
                          setProvVlan('')
                          setProvPort('')
                          setProvActivationDate('')
                          setProvPlan('')
                          setProvPlanPeriod('MONTHLY')
                          setProvFmc('')
                          setProvCustomerFormOpen(true)
                          return
                        }
                        toggleChecklistItem(item.key)
                      }}
                      disabled={provSaving || (isDetailsItem && hasDetails)}
                      className="shrink-0 mt-0.5 transition-colors disabled:opacity-40"
                    >
                      {checked ? (
                        <CheckSquare size={18} className="text-emerald-600" />
                      ) : (
                        <Square size={18} className="text-gray-300 hover:text-gray-400" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold ${checked ? 'text-emerald-700' : 'text-gray-700'}`}>
                        {idx + 1}. {item.label}
                      </p>
                      {checked && (
                        <p className="text-[10px] text-emerald-500 font-semibold">Completed</p>
                      )}
                      {isDetailsItem && hasDetails && (
                        <p className="text-[10px] text-gray-400">Details saved for {task!.provisionedName}</p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Complete button when all checked and not yet provisioned */}
            {allChecked && !isProvisioned && (
              <div className="px-4 py-3 border-t border-gray-100">
                <button onClick={handleCompleteProvisioning} disabled={provSaving}
                  className="w-full py-3 text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-all active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg,#10b981,#059669)', boxShadow: '0 4px 14px rgba(16,185,129,0.3)' }}>
                  {provSaving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  Complete Provisioning
                </button>
              </div>
            )}
          </div>
        )}

        {/* Onboarding Details Form */}
        {provCustomerFormOpen && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
              <Wifi size={14} className="text-emerald-600" />
              <p className="text-xs font-black text-emerald-800">Customer Provisioning Details</p>
            </div>
            <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Customer Information</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={lbl}>Name <span className="text-red-400">*</span></label>
                  <input value={provCustomerName} onChange={e => setProvCustomerName(e.target.value)} className={inp} />
                </div>
                <div>
                  <label className={lbl}>Phone <span className="text-red-400">*</span></label>
                  <input value={provCustomerPhone} onChange={e => setProvCustomerPhone(e.target.value)} className={inp} />
                </div>
                <div>
                  <label className={lbl}>Email</label>
                  <input value={provCustomerEmail} onChange={e => setProvCustomerEmail(e.target.value)} type="email" className={inp} />
                </div>
                <div>
                  <label className={lbl}>City</label>
                  <input value={provCustomerCity} onChange={e => setProvCustomerCity(e.target.value)} className={inp} />
                </div>
              </div>
              <div>
                <label className={lbl}>Address</label>
                <textarea value={provCustomerAddress} onChange={e => setProvCustomerAddress(e.target.value)} rows={2}
                  className={inp + ' resize-none'} />
              </div>
              <div>
                <label className={lbl}>Building / Landmark</label>
                <BuildingPicker
                  value={provBuilding}
                  onChange={val => setProvBuilding(val)}
                  inputWrapClass={iw}
                />
              </div>

              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest pt-2">Service Details</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={lbl}>Service Number <span className="text-red-400">*</span></label>
                  <input value={provServiceNumber} onChange={e => setProvServiceNumber(e.target.value)} className={inp} placeholder="0471-1234567" />
                </div>
                <div>
                  <label className={lbl}>Billing Account No</label>
                  <input value={provBillingAccountNo} onChange={e => setProvBillingAccountNo(e.target.value)} className={inp} />
                </div>
                <div>
                  <label className={lbl}>Category</label>
                  <select value={provCategory} onChange={e => setProvCategory(e.target.value)} className={inp}>
                    <option value="Individual">Individual</option>
                    <option value="SME">SME</option>
                    <option value="Enterprise">Enterprise</option>
                    <option value="Government">Government</option>
                  </select>
                </div>
                <div>
                  <label className={lbl}>Exchange Code</label>
                  <input value={provExchangeCode} onChange={e => setProvExchangeCode(e.target.value)} className={inp} />
                </div>
                <div>
                  <label className={lbl}>Sub Service Type</label>
                  <input value={provSubServiceType} onChange={e => setProvSubServiceType(e.target.value)} className={inp} />
                </div>
                <div>
                  <label className={lbl}>Plan <span className="text-red-400">*</span></label>
                  {!useCustomPlan ? (
                    <div className="space-y-1">
                      <select
                        value={provPlan}
                        onChange={e => {
                          const found = availablePlans.find(p => p.name === e.target.value)
                          if (found) { setProvPlan(found.name); setProvFmc(String(found.fmc)); setProvPlanPeriod(found.period) }
                          else { setProvPlan(''); setProvFmc('') }
                        }}
                        className={inp + ' appearance-none cursor-pointer'}>
                        <option value="">— Choose a plan —</option>
                        {availablePlans.map(p => (
                          <option key={`${p.name}|${p.fmc}`} value={p.name}>
                            {p.name} — ₹{p.fmc}/{p.period}
                          </option>
                        ))}
                      </select>
                      <button onClick={() => { setUseCustomPlan(true); setProvPlan(''); setProvFmc('') }}
                        className="text-[11px] font-bold text-blue-500 hover:text-blue-700 transition-colors">
                        + Enter custom plan
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="grid grid-cols-2 gap-2">
                        <input value={provPlan} onChange={e => setProvPlan(e.target.value)}
                          className={inp} placeholder="Plan name" />
                        <input value={provFmc} onChange={e => setProvFmc(e.target.value)} type="number"
                          className={inp} placeholder="FMC (₹/mo)" />
                      </div>
                      <button onClick={() => { setUseCustomPlan(false); setProvPlan(''); setProvFmc('') }}
                        className="text-[11px] font-bold text-gray-400 hover:text-gray-600 transition-colors">
                        ← Back to plan list
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest pt-2">Network Details</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={lbl}>OLT IP</label>
                  <input value={provOltIp} onChange={e => setProvOltIp(e.target.value)} className={inp} />
                </div>
                <div>
                  <label className={lbl}>BB User ID</label>
                  <input value={provBbUserId} onChange={e => setProvBbUserId(e.target.value)} className={inp} />
                </div>
                <div>
                  <label className={lbl}>VLAN</label>
                  <input value={provVlan} onChange={e => setProvVlan(e.target.value)} type="number" className={inp} />
                </div>
                <div>
                  <label className={lbl}>Port</label>
                  <input value={provPort} onChange={e => setProvPort(e.target.value)} type="number" className={inp} />
                </div>
                <div>
                  <label className={lbl}>VOIP Password</label>
                  <input value={provVoipPassword} onChange={e => setProvVoipPassword(e.target.value)} className={inp} />
                </div>
                <div>
                  <label className={lbl}>Activation Date</label>
                  <input value={provActivationDate} onChange={e => setProvActivationDate(e.target.value)} className={inp} placeholder="DD/MM/YYYY HH:MM:SS" />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={handleCustomerProvisioned} disabled={provSaving}
                  className="flex-1 py-3 text-white font-black rounded-xl text-sm flex items-center justify-center gap-1.5 disabled:opacity-60 transition-all active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg,#10b981,#059669)', boxShadow: '0 4px 12px rgba(16,185,129,0.25)' }}>
                  {provSaving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                  Save & Add to Customer List
                </button>
                <button onClick={() => setProvCustomerFormOpen(false)}
                  className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold rounded-xl text-sm transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {isProvisioned && task!.provisionedName && (
          <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
              <CheckCircle2 size={14} className="text-emerald-600" />
              <p className="text-xs font-black text-emerald-800">Provisioned Customer Summary</p>
            </div>
            <div className="p-4 space-y-2">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                <div><span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block">Name</span><p className="text-sm font-bold text-gray-700">{task!.provisionedName}</p></div>
                <div><span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block">Phone</span><p className="text-sm font-bold text-gray-700">+91 {task!.provisionedPhone}</p></div>
                {task!.provisionedEmail && <div className="col-span-2"><span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block">Email</span><p className="text-sm text-gray-700">{task!.provisionedEmail}</p></div>}
                {task!.provisionedAddress && <div className="col-span-2"><span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block">Address</span><p className="text-xs text-gray-600">{task!.provisionedAddress}{task!.provisionedCity ? `, ${task!.provisionedCity}` : ''}</p></div>}
              </div>
              <div className="border-t border-gray-100 pt-2">
                <p className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1">Service Details</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {task!.serviceNumber && <div><span className="text-[9px] text-gray-400">Service No</span><p className="text-xs font-bold text-gray-700">{task!.serviceNumber}</p></div>}
                  {task!.exchangeCode && <div><span className="text-[9px] text-gray-400">Exchange</span><p className="text-xs font-bold text-gray-700">{task!.exchangeCode}</p></div>}
                  {task!.bbUserId && <div><span className="text-[9px] text-gray-400">BB User ID</span><p className="text-xs font-bold text-gray-700">{task!.bbUserId}</p></div>}
                  {task!.oltIp && <div><span className="text-[9px] text-gray-400">OLT IP</span><p className="text-xs font-bold text-gray-700">{task!.oltIp}</p></div>}
                  {task!.vlan !== undefined && task!.vlan !== null && <div><span className="text-[9px] text-gray-400">VLAN</span><p className="text-xs font-bold text-gray-700">{task!.vlan}</p></div>}
                  {task!.port !== undefined && task!.port !== null && <div><span className="text-[9px] text-gray-400">Port</span><p className="text-xs font-bold text-gray-700">{task!.port}</p></div>}
                  {task!.voipPassword !== undefined && task!.voipPassword !== null && <div><span className="text-[9px] text-gray-400">VoIP Password</span><p className="text-xs font-bold text-gray-700">{task!.voipPassword}</p></div>}
                  {task!.billingAccountNo !== undefined && <div><span className="text-[9px] text-gray-400">Billing A/C</span><p className="text-xs font-bold text-gray-700">{task!.billingAccountNo}</p></div>}
                </div>
              </div>
              {task!.provisioningPlan && <div className="col-span-2 mt-1 px-3 py-2 bg-blue-50 rounded-xl border border-blue-100"><span className="text-[9px] font-black text-blue-400 uppercase tracking-widest block">Plan</span><p className="text-sm font-black text-blue-700">{task!.provisioningPlan} · ₹{task!.provisioningFmc}/mo</p></div>}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderInstallationSection = () => {
    if (task!.provisioningStage !== 'provisioned') return null
    const phase = (task!.installationPhase ?? 'pending') as InstPhase
    const phaseIdx = INST_PHASES.indexOf(phase)
    const nextPhase = INST_NEXT[phase]
    const isInstDone = phase === 'completed'
    const canManageInst = isAdmin

    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50" style={{ background: 'linear-gradient(135deg,#fdf4ff,#eff6ff)' }}>
          <Wifi size={13} className="text-violet-600" />
          <p className="text-[10px] font-black text-violet-700 uppercase tracking-widest">Installation Workflow</p>
          <span className="ml-auto text-[10px] font-black px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">{INST_PHASE_LABELS[phase]}</span>
        </div>

        {/* ── BEGIN INSTALLATION PROMPT ── */}
        {phase === 'pending' && (
          <div className="p-6 text-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3"
              style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)', boxShadow: '0 4px 14px rgba(139,92,246,0.25)' }}>
              <Wifi size={28} className="text-white" />
            </div>
            <p className="text-sm font-black text-gray-800 mb-1">Provisioning Complete</p>
            <p className="text-xs text-gray-500 mb-5 max-w-xs mx-auto">Customer has been successfully provisioned. Click below to begin the installation workflow.</p>
            {canManageInst ? (
              <button onClick={() => handleInstPhase('assigned')} disabled={instPhaseSaving}
                className="px-8 py-3 text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 mx-auto disabled:opacity-60 transition-all active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)', boxShadow: '0 4px 14px rgba(139,92,246,0.3)' }}>
                {instPhaseSaving ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
                Begin Installation
              </button>
            ) : (
              <p className="text-xs text-gray-400 font-semibold">Waiting for admin to begin installation…</p>
            )}
          </div>
        )}

        {/* ── ACTIVE WORKFLOW (shown once phase moves past pending) ── */}
        {phase !== 'pending' && (
          <>
            {/* Progress bar */}
            <div className="px-4 pt-4 pb-2">
              <div className="flex items-center gap-0.5 overflow-x-auto">
                {INST_PHASES.map((ph, i) => (
                  <div key={ph} className="flex items-center gap-0.5 shrink-0">
                    <div className={`w-2 h-2 rounded-full ${i <= phaseIdx ? 'bg-violet-600' : 'bg-gray-200'}`} />
                    {i < INST_PHASES.length - 1 && <div className={`w-6 h-0.5 ${i < phaseIdx ? 'bg-violet-400' : 'bg-gray-200'}`} />}
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-gray-400 mt-1 font-semibold">{INST_PHASE_LABELS[phase]}</p>
            </div>

            <div className="p-4 space-y-3">

              {/* Assigned engineer display */}
              {task!.installationEngineerName ? (
                <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-xl border border-violet-100">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-black shrink-0"
                    style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)' }}>
                    {task!.installationEngineerName.split(' ').map((w: string) => w[0]).join('').slice(0,2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-800">{task!.installationEngineerName}</p>
                    <p className="text-[10px] text-violet-500 font-semibold">Installation Engineer</p>
                  </div>
                  <UserCheck size={15} className="text-violet-400 shrink-0" />
                </div>
              ) : (
                canManageInst && (
                  <div className="space-y-2">
                    <button onClick={() => setShowInstEngPicker(v => !v)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-xl text-sm font-bold text-violet-700 transition-colors">
                      <span className="flex items-center gap-2"><UserCheck size={13} /> Assign Installation Engineer</span>
                      <ChevronDown size={13} className={`transition-transform ${showInstEngPicker ? 'rotate-180' : ''}`} />
                    </button>
                    {showInstEngPicker && (
                      <div className="rounded-xl border border-gray-200 overflow-hidden shadow-md bg-white">
                        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 bg-gray-50">
                          <User size={12} className="text-gray-400" />
                          <input value={instEngSearch} onChange={e => setInstEngSearch(e.target.value)}
                            placeholder="Search engineer…" autoFocus
                            className="flex-1 text-sm outline-none text-gray-700 placeholder-gray-400 bg-transparent" />
                          {instEngSearch && <button onClick={() => setInstEngSearch('')}><X size={12} className="text-gray-400" /></button>}
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {filteredInstEngs.length === 0
                            ? <p className="py-5 text-center text-xs text-gray-400">No match</p>
                            : filteredInstEngs.map((eng, i) => (
                              <button key={eng.id} disabled={assigningInstEng} onClick={() => handleAssignInstEng(eng)}
                                className={`w-full flex items-center gap-3 px-3 py-3 hover:bg-violet-50 text-left transition-colors disabled:opacity-60 ${i > 0 ? 'border-t border-gray-50' : ''}`}>
                                <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-[10px] font-black shrink-0"
                                  style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)' }}>
                                  {eng.name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[13px] font-bold text-gray-800 truncate">{eng.name}</p>
                                  <p className="text-[10px] text-gray-400 truncate">{eng.location}</p>
                                </div>
                                {assigningInstEng ? <Loader2 size={12} className="animate-spin text-violet-400 shrink-0" /> : <ArrowRight size={12} className="text-gray-300 shrink-0" />}
                              </button>
                            ))
                          }
                        </div>
                      </div>
                    )}
                  </div>
                )
              )}

              {/* Load Customer Data */}
              {task!.installationEngineerName && !task!.installationDataLoaded && (
                <button onClick={handleLoadInstData} disabled={instVerifSaving}
                  className="w-full py-3 text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-all active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', boxShadow: '0 4px 14px rgba(37,99,235,0.3)' }}>
                  {instVerifSaving ? <Loader2 size={14} className="animate-spin" /> : <Package size={14} />}
                  Load Customer Data
                </button>
              )}

          {/* Customer Data Verification - Accordion Style */}
          {task!.installationDataLoaded && (() => {
            const toggleSection = (section: string) => {
              setOpenInstSections(prev => ({ ...prev, [section]: !prev[section] }))
            }

            const verificationGroups = [
              {
                key: 'customer',
                title: 'Customer Information',
                icon: Users,
                fields: [
                  { key: 'provisionedName', label: 'Company Name (Customer Name)', value: task!.provisionedName || '—' },
                  { key: 'provisionedPhone', label: 'Mobile Number', value: task!.provisionedPhone ? `+91 ${task!.provisionedPhone}` : '—' },
                  { key: 'provisionedEmail', label: 'Email Address', value: task!.provisionedEmail || '—' },
                  { key: 'provisionedAddress', label: 'Installation Address', value: task!.provisionedAddress || '—' },
                  { key: 'provisionedBuilding', label: 'Building / Landmark', value: task!.provisionedBuilding || '—' },
                  { key: 'provisionedCity', label: 'City', value: task!.provisionedCity || '—' },
                ]
              },
              {
                key: 'connection',
                title: 'Connection Parameters',
                icon: Network,
                fields: [
                  { key: 'serviceNumber', label: 'Service Number', value: task!.serviceNumber || '—' },
                  { key: 'bbUserId', label: 'Broadband User ID', value: task!.bbUserId || '—' },
                  { key: 'voipPassword', label: 'VoIP Password', value: task!.voipPassword || '—' },
                  { key: 'activationDate', label: 'Activation Date', value: task!.activationDate || '—' },
                ]
              },
              {
                key: 'technical',
                title: 'Technical Settings',
                icon: Settings,
                fields: [
                  { key: 'oltIp', label: 'OLT IP Address', value: task!.oltIp || '—' },
                  { key: 'vlan', label: 'VLAN ID', value: task!.vlan !== undefined && task!.vlan !== null ? String(task!.vlan) : '—' },
                  { key: 'port', label: 'Port Number', value: task!.port !== undefined && task!.port !== null ? String(task!.port) : '—' },
                  { key: 'exchangeCode', label: 'Exchange Code', value: task!.exchangeCode || '—' },
                  { key: 'category', label: 'Category Code', value: task!.category || '—' },
                  { key: 'subServiceType', label: 'Service Type', value: task!.subServiceType || '—' },
                ]
              },
              {
                key: 'plan',
                title: 'Plan & Billing',
                icon: CreditCard,
                fields: [
                  { key: 'provisioningPlan', label: 'Subscription Plan', value: task!.provisioningPlan || '—' },
                  { key: 'provisioningPlanPeriod', label: 'Plan Period', value: task!.provisioningPlanPeriod || '—' },
                  { key: 'billingAccountNo', label: 'Billing Account No', value: task!.billingAccountNo !== undefined && task!.billingAccountNo !== null ? String(task!.billingAccountNo) : '—' },
                  { key: 'provisioningFmc', label: 'Monthly FMC', value: task!.provisioningFmc !== undefined && task!.provisioningFmc !== null ? `₹${task!.provisioningFmc}` : '—' },
                ]
              }
            ]

            const allFields = verificationGroups.flatMap(g => g.fields)
            const verifiedCount = allFields.filter(f => task!.installationVerification?.[f.key]).length

            return (
              <div className="rounded-xl border border-blue-200 overflow-hidden bg-white">
                <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserCheck size={13} className="text-blue-600" />
                    <p className="text-[10px] font-black text-blue-800 uppercase tracking-widest">Verify All Customer Data</p>
                  </div>
                  <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-blue-100 text-blue-600">
                    {verifiedCount}/{allFields.length} verified
                  </span>
                </div>

                {/* Accordion Sections */}
                <div className="p-3 space-y-2">
                  {verificationGroups.map(group => {
                    const isOpen = openInstSections[group.key]
                    const groupVerified = group.fields.filter(f => task!.installationVerification?.[f.key]).length
                    const isComplete = groupVerified === group.fields.length

                    return (
                      <div key={group.key} className={`rounded-lg overflow-hidden border ${isComplete ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white'}`}>
                        {/* Section Header */}
                        <button
                          type="button"
                          onClick={() => toggleSection(group.key)}
                          className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50/50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <group.icon size={14} className={isComplete ? 'text-emerald-500' : 'text-slate-500'} />
                            <span className={`text-xs font-bold ${isComplete ? 'text-emerald-700' : 'text-slate-700'}`}>{group.title}</span>
                            <span className="text-[10px] text-slate-400">({groupVerified}/{group.fields.length})</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {isComplete && <CheckCircle2 size={12} className="text-emerald-500" />}
                            {isOpen ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                          </div>
                        </button>

                        {/* Section Content */}
                        {isOpen && (
                          <div className="px-3 pb-3 space-y-1 border-t border-slate-100/50">
                            {group.fields.map(field => {
                              const isChecked = !!task!.installationVerification?.[field.key]
                              const isEmpty = field.value === '—'
                              return (
                                <div
                                  key={field.key}
                                  className={`flex items-start gap-2.5 p-2 rounded-lg transition-all ${
                                    isChecked ? 'bg-emerald-50/50' : isEmpty ? 'bg-amber-50/50' : 'bg-slate-50/50'
                                  }`}
                                >
                                  <button
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToggleInstVerif(field.key); }}
                                    disabled={instVerifSaving}
                                    className="shrink-0 mt-0.5 disabled:opacity-40"
                                  >
                                    {isChecked
                                      ? <CheckSquare size={16} className="text-emerald-600" />
                                      : <Square size={16} className="text-gray-300 hover:text-gray-400" />
                                    }
                                  </button>
                                  <div className="min-w-0 flex-1">
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wide block">{field.label}</span>
                                    <span className={`text-xs font-bold ${
                                      isChecked ? 'text-slate-600 line-through opacity-60' :
                                      isEmpty ? 'text-amber-600 italic' : 'text-slate-800'
                                    }`}>
                                      {field.value}
                                      {isEmpty && <span className="text-[9px] font-normal text-amber-500 ml-1">(not provided)</span>}
                                    </span>
                                  </div>
                                  {isChecked && <CheckCircle2 size={12} className="text-emerald-500 shrink-0 self-center" />}
                                  {isEmpty && !isChecked && <span className="text-[9px] text-amber-500 shrink-0">⚠️</span>}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })()}

          {/* Speed Test Card - shown in speed_test phase or if test exists */}
          {(phase === 'speed_test' || phase === 'pending_approval' || phase === 'completed' || task!.speedTestDownload) && (
            <div className="rounded-xl border border-indigo-200 overflow-hidden bg-white">
              <div className="px-4 py-2.5 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gauge size={13} className="text-indigo-600" />
                  <p className="text-[10px] font-black text-indigo-800 uppercase tracking-widest">Device Connectivity & Speed Test</p>
                </div>
                {testDlSpeed && (
                  <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-600">
                    ✓ Completed
                  </span>
                )}
              </div>

              <div className="p-4 space-y-3">
                {/* Instructions */}
                {phase === 'speed_test' && !testDlSpeed && testingState === 'idle' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs font-bold text-amber-800">
                      📱 Connect to Customer's BSNL Wi-Fi
                    </p>
                    <p className="text-[10px] text-amber-700 mt-1">
                      Ensure your phone is connected to the customer's BSNL modem Wi-Fi network before starting the speed test.
                    </p>
                  </div>
                )}

                {/* Speed Test UI */}
                {testingState === 'idle' && !testDlSpeed && phase === 'speed_test' && (
                  <div className="text-center space-y-3">
                    <Wifi size={32} className="text-indigo-400 mx-auto" />
                    <div>
                      <p className="text-xs font-bold text-gray-800">Ready to Test</p>
                      <p className="text-[10px] text-gray-500">Test download, upload, and ping speeds</p>
                    </div>
                    <button
                      onClick={runSpeedTest}
                      disabled={instPhaseSaving}
                      className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-xs font-black rounded-xl shadow-md active:scale-95 transition-all flex items-center gap-2 mx-auto"
                    >
                      <Gauge size={14} /> Start Speed Test
                    </button>
                  </div>
                )}

                {/* Testing Progress */}
                {testingState !== 'idle' && testingState !== 'done' && (
                  <div className="text-center space-y-3">
                    <Loader2 size={28} className="animate-spin text-indigo-500 mx-auto" />
                    <div>
                      <p className="text-xs font-black text-indigo-900 animate-pulse">
                        {testingState === 'checking_isp' && '🔍 Checking ISP Provider...'}
                        {testingState === 'ping' && '⚡ Testing Latency (Ping)...'}
                        {testingState === 'download' && '📥 Testing Download Speed...'}
                        {testingState === 'upload' && '📤 Testing Upload Speed...'}
                        {testingState === 'saving' && '💾 Saving Results...'}
                      </p>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden max-w-[200px] mx-auto">
                      <div className="bg-indigo-500 h-full rounded-full transition-all" style={{ width: `${speedTestProgress}%` }} />
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {speedTestError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={14} className="text-red-500 shrink-0 mt-0.5" />
                      <p className="text-xs font-semibold text-red-700">{speedTestError}</p>
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer pt-2 border-t border-red-100">
                      <input
                        type="checkbox"
                        checked={ispOverride}
                        onChange={e => setIspOverride(e.target.checked)}
                        className="mt-0.5 rounded border-red-300 text-red-600"
                      />
                      <span className="text-[10px] text-red-800">Override ISP check (use if network is misidentified)</span>
                    </label>
                    {ispOverride && (
                      <button
                        onClick={runSpeedTest}
                        className="w-full py-2 bg-red-500 text-white text-xs font-black rounded-lg"
                      >
                        Retry with Override
                      </button>
                    )}
                  </div>
                )}

                {/* Results Display */}
                {(testDlSpeed || testingState === 'done') && (
                  <div className="space-y-3">
                    {/* Test Summary Header */}
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 flex items-center justify-between">
                      <span className="text-xs font-bold text-emerald-700 flex items-center gap-1.5">
                        <CheckCircle2 size={14} /> Speed Test Completed
                      </span>
                      {task!.speedTestTimestamp && (
                        <span className="text-[10px] text-emerald-600">
                          {new Date(task!.speedTestTimestamp).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-emerald-50/70 border border-emerald-200 rounded-lg p-2 text-center">
                        <p className="text-[9px] text-emerald-600 font-bold uppercase tracking-wide">Download</p>
                        <p className="text-xl font-black text-emerald-700">{testDlSpeed?.toFixed(1) ?? '--'} <span className="text-xs font-bold">Mbps</span></p>
                      </div>
                      <div className="bg-blue-50/70 border border-blue-200 rounded-lg p-2 text-center">
                        <p className="text-[9px] text-blue-600 font-bold uppercase tracking-wide">Upload</p>
                        <p className="text-xl font-black text-blue-700">{testUlSpeed?.toFixed(1) ?? '--'} <span className="text-xs font-bold">Mbps</span></p>
                      </div>
                      <div className="bg-violet-50/70 border border-violet-200 rounded-lg p-2 text-center">
                        <p className="text-[9px] text-violet-600 font-bold uppercase tracking-wide">Ping</p>
                        <p className="text-xl font-black text-violet-700">{testPing ?? '--'} <span className="text-xs font-bold">ms</span></p>
                      </div>
                    </div>

                    {/* Connection Details */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1.5">
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Connection Details</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400 font-semibold">ISP:</span>
                          <span className="font-bold text-slate-700">{testIsp || 'Unknown'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400 font-semibold">Client IP:</span>
                          <span className="font-mono font-bold text-slate-700">{testIp || '0.0.0.0'}</span>
                        </div>
                      </div>
                      {/* GPS Location */}
                      {(testLat !== null && testLng !== null) || (task!.speedTestLatitude !== undefined && task!.speedTestLongitude !== undefined) ? (
                        <div className="pt-2 border-t border-slate-100">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <MapPin size={12} className="text-rose-500" />
                              <span className="text-[10px] text-slate-500 font-semibold">Test Location:</span>
                              <span className="text-[10px] font-mono font-bold text-slate-700">
                                {(testLat ?? task!.speedTestLatitude)?.toFixed(6)}, {(testLng ?? task!.speedTestLongitude)?.toFixed(6)}
                              </span>
                            </div>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${testLat ?? task!.speedTestLatitude},${testLng ?? task!.speedTestLongitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-0.5 rounded-md"
                            >
                              Open Map
                            </a>
                          </div>
                        </div>
                      ) : (
                        <div className="pt-2 border-t border-slate-100">
                          <p className="text-[10px] text-amber-600">📍 GPS location not captured (permission may have been denied)</p>
                        </div>
                      )}
                    </div>

                    {/* Retest button */}
                    {phase === 'speed_test' && (
                      <button
                        onClick={runSpeedTest}
                        disabled={testingState !== 'idle' && testingState !== 'done'}
                        className="w-full py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-black rounded-lg border border-indigo-200 transition-colors flex items-center justify-center gap-2"
                      >
                        <RefreshCw size={13} /> Run Test Again (Overwrite Previous)
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Phase progression — gated behind all verification ticks */}
          {!isInstDone && nextPhase && task!.installationEngineerName && task!.installationDataLoaded && (
            (() => {
              const verif = task!.installationVerification ?? {}
              // All field keys from the accordion verification groups
              const allFieldKeys = [
                'provisionedName', 'provisionedPhone', 'provisionedEmail', 'provisionedAddress', 'provisionedBuilding', 'provisionedCity',
                'serviceNumber', 'bbUserId', 'voipPassword', 'activationDate',
                'oltIp', 'vlan', 'port', 'exchangeCode', 'category', 'subServiceType',
                'provisioningPlan', 'provisioningPlanPeriod', 'billingAccountNo', 'provisioningFmc'
              ]
              const allTicked = allFieldKeys.every(k => verif[k])
              const verifiedCount = allFieldKeys.filter(k => verif[k]).length

              // Speed test check for speed_test phase
              const isSpeedTestDone = !!task!.speedTestDownload
              const canProceed = phase === 'speed_test' ? (allTicked && isSpeedTestDone) : allTicked

              return (
                <>
                  {!allTicked && (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                      <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                      <p className="text-[11px] font-bold text-amber-700">Complete all verification checks before proceeding ({verifiedCount}/{allFieldKeys.length})</p>
                    </div>
                  )}
                  {phase === 'speed_test' && allTicked && !isSpeedTestDone && (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                      <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                      <p className="text-[11px] font-bold text-amber-700">Complete speed test before proceeding</p>
                    </div>
                  )}
                  <button onClick={() => handleInstPhase(nextPhase)} disabled={instPhaseSaving || !canProceed}
                    className="w-full py-3 text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                    style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)', boxShadow: '0 4px 14px rgba(139,92,246,0.3)' }}>
                    {instPhaseSaving ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                    Move to {INST_PHASE_LABELS[nextPhase]}
                  </button>
                </>
              )
            })()
          )}

          {/* Admin Approval Section - for pending_approval phase */}
          {phase === 'pending_approval' && isAdmin && (
            <div className="rounded-xl border border-purple-200 overflow-hidden bg-purple-50/50">
              <div className="px-4 py-3 bg-purple-100 border-b border-purple-200">
                <p className="text-xs font-black text-purple-800 uppercase tracking-wider">Admin Review Required</p>
                <p className="text-[10px] text-purple-600 mt-0.5">Installation and speed test completed. Approve to close ticket.</p>
              </div>
              <div className="p-4 space-y-3">
                {/* Feedback Link */}
                <FeedbackLinkCard task={task!} />

                {/* Approve Button */}
                <button
                  onClick={handleApprove}
                  disabled={approvingId === task!.id}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white font-black rounded-xl shadow-md active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {approvingId === task!.id ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} /> Approve & Close Ticket
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Site photos for installation */}
          {(phase === 'site_visit' || phase === 'work_in_progress') && !isAdmin && (
            <div className="space-y-2">
              <p className="text-xs font-black text-gray-500">Upload Site Photos</p>
              {savedUrls.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {savedUrls.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                      className="w-16 h-16 rounded-xl overflow-hidden border border-gray-200">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <label className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl cursor-pointer text-xs font-black text-gray-600 transition-colors">
                  <ImagePlus size={13} /> Add Photos
                  <input type="file" multiple accept="image/*" className="hidden" onChange={handlePickPhotos} />
                </label>
                {pendingPhotos.length > 0 && (
                  <button onClick={handleUploadPhotos} disabled={uploading}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-black text-white disabled:opacity-60"
                    style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                    {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                    {uploading ? 'Uploading…' : `Upload ${pendingPhotos.length}`}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Completion banner */}
          {isInstDone && (
            <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-200">
              <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
              <div>
                <p className="text-sm font-black text-emerald-800">Installation Completed</p>
                {task!.installationCompletedAt && (
                  <p className="text-[10px] text-emerald-600 mt-0.5">
                    {task!.installationCompletedAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Checklist & feedback reuse for installation */}
          {!isInstDone && task!.installationEngineerName && phase !== 'assigned' && (
            <div className="pt-2 border-t border-gray-50">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Phase Progress</p>
              <div className="flex flex-wrap gap-1.5">
                {INST_PHASES.slice(0, phaseIdx + 1).map(ph => (
                  <span key={ph} className={`text-[10px] font-black px-2 py-0.5 rounded-full ${ph === phase ? 'bg-violet-100 text-violet-700' : 'bg-emerald-50 text-emerald-600'}`}>
                    {ph === phase ? '▶ ' : '✓ '}{INST_PHASE_LABELS[ph]}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        </>
      )}
      </div>
    )
  }

  const statusSteps: TaskStatus[] = ['open','assigned','in_progress','site_visit','work_in_progress','pending_approval','pending_feedback','closed']
  const currentIdx = statusSteps.indexOf(task.status)

  return (
    <div className="min-h-screen bg-gray-50/60 pb-32">

      {/* ── JIRA-style top bar ── */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="flex items-center gap-3 px-4 py-3 max-w-7xl mx-auto">
          <button onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors shrink-0">
            <ArrowLeft size={15} className="text-gray-600" />
          </button>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-[11px] font-mono text-gray-400 shrink-0">{task.taskNumber}</span>
            <span className="text-gray-300">/</span>
            <p className="text-sm font-bold text-gray-800 truncate">{task.customerName}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`flex items-center gap-1 text-[11px] font-black px-2.5 py-1 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
              {s.icon} {s.label}
            </span>
            <button onClick={load} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors">
              <RefreshCw size={13} className="text-gray-500" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pt-5 lg:flex lg:gap-6 lg:items-start">

        {/* ══════════════════ LEFT / MAIN COLUMN ══════════════════ */}
        <div className="flex-1 min-w-0 space-y-4">

          {/* ── Hero header ── */}
          {(() => {
            // ── Phase theming for enquiry tasks ─────────────────────────────
            const isEnq = task.type === 'enquiry'
            const _isProvisioningStage = isEnq && !!task.provisioningStage && task.provisioningStage !== 'pending'
            const _isProvisioned       = isEnq && task.provisioningStage === 'provisioned'
            const _isInstallation      = isEnq && !!task.installationPhase && task.installationPhase !== 'pending'
            const _isComplete          = isEnq && (task.installationPhase === 'completed' || task.status === 'closed')

            const docketPhase =
              !isEnq           ? null :
              _isComplete      ? 'installation' :
              _isInstallation  ? 'installation' :
              _isProvisioned   ? 'provisioning' :
              _isProvisioningStage ? 'provisioning' :
              (task.feasibilityApproved) ? 'provisioning' :
              (task.status === 'site_visit' || task.status === 'work_in_progress' || task.status === 'pending_approval') ? 'feasibility' :
              'enquiry'

            const phaseTheme = {
              enquiry:      { grad: 'linear-gradient(135deg,#1e3a5f 0%,#1d4ed8 100%)', glow: '#2563eb', label: '📋 Enquiry Phase',      sub: 'Docket Raised & Verification' },
              feasibility:  { grad: 'linear-gradient(135deg,#2e1065 0%,#7c3aed 100%)', glow: '#7c3aed', label: '🔍 Feasibility Phase',   sub: 'Site Study & Admin Review'    },
              provisioning: { grad: 'linear-gradient(135deg,#431407 0%,#c2410c 100%)', glow: '#ea580c', label: '📦 Provisioning Phase',  sub: 'Plan Selection & Onboarding'  },
              installation: { grad: 'linear-gradient(135deg,#052e16 0%,#15803d 100%)', glow: '#059669', label: '🔧 Installation Phase',  sub: 'Fibre Lay & Commissioning'    },
            }
            const heroGrad = docketPhase ? phaseTheme[docketPhase].grad : `linear-gradient(135deg,#060f1e 0%,#0d2040 100%)`
            const heroGlow = docketPhase ? phaseTheme[docketPhase].glow : t.dot
            const phaseLabel = docketPhase ? phaseTheme[docketPhase].label : null
            const phaseSub   = docketPhase ? phaseTheme[docketPhase].sub   : null

            // Phase step indicators for enquiry
            const docketPhases = ['enquiry','feasibility','provisioning','installation'] as const
            const docketPhaseIdx = docketPhase ? docketPhases.indexOf(docketPhase) : -1

            return (
              <div className="relative overflow-hidden rounded-2xl text-white transition-all duration-700"
                style={{ background: heroGrad }}>
                {/* Glow orb */}
                <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full pointer-events-none"
                  style={{ background: `radial-gradient(circle,${heroGlow}55 0%,transparent 70%)`, filter: 'blur(28px)' }} />
                {/* Priority bar */}
                <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: p.bar }} />

                <div className="relative z-10 p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-2xl shrink-0 flex items-center justify-center text-white font-black text-lg"
                      style={{ background: 'rgba(255,255,255,0.15)', boxShadow: `0 6px 20px ${heroGlow}55` }}>
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h1 className="text-xl font-black text-white leading-tight">{task.customerName}</h1>
                      <p className="text-[11px] font-mono text-white/50 mt-0.5">{task.taskNumber}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {phaseLabel ? (
                          <span className="flex items-center gap-1.5 text-[11px] font-black px-3 py-1.5 rounded-full text-white"
                            style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
                            {phaseLabel}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full text-white"
                            style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)' }}>
                            {t.icon} {displayType}
                          </span>
                        )}
                        <span className={`flex items-center gap-1 text-[11px] font-black px-3 py-1.5 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
                          {s.icon} {s.label}
                        </span>
                        <span className={`text-[11px] font-black px-3 py-1.5 rounded-full ${p.badge} ${p.badgeText}`}>
                          {p.label} Priority
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Progress section */}
                  {task.status !== 'cancelled' && (
                    <div className="mt-5">
                      {docketPhase ? (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] text-white/60 font-semibold uppercase tracking-widest">{phaseSub}</span>
                            <span className="text-[10px] text-white/50 font-semibold">Phase {docketPhaseIdx + 1} of {docketPhases.length}</span>
                          </div>
                          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-700 bg-white/70"
                              style={{ width: `${Math.round(((docketPhaseIdx + 1) / docketPhases.length) * 100)}%` }} />
                          </div>
                          <div className="flex items-center gap-2 mt-3 overflow-x-auto scrollbar-hide">
                            {docketPhases.map((ph, i) => {
                              const isDone = i < docketPhaseIdx
                              const isCur  = i === docketPhaseIdx
                              return (
                                <div key={ph} className="flex items-center gap-1.5 shrink-0">
                                  <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black transition-all ${
                                    isCur  ? 'bg-white/25 text-white' :
                                    isDone ? 'bg-white/15 text-white/60' :
                                    'text-white/25'
                                  }`}>
                                    {isDone && <CheckCircle2 size={9} />}
                                    {ph.charAt(0).toUpperCase() + ph.slice(1)}
                                  </div>
                                  {i < docketPhases.length - 1 && (
                                    <div className={`w-4 h-px ${i < docketPhaseIdx ? 'bg-white/40' : 'bg-white/15'}`} />
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] text-white/50 font-semibold uppercase tracking-widest">Workflow Progress</span>
                            <span className="text-[10px] text-white/50 font-semibold">{Math.round(((currentIdx + 1) / statusSteps.length) * 100)}%</span>
                          </div>
                          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-700"
                              style={{ width: `${Math.round(((currentIdx + 1) / statusSteps.length) * 100)}%`, background: t.grad }} />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })()}

          {/* ── Docket Workflow Sections (enquiry only) ── */}
          {task.type === 'enquiry' && (() => {
            // Static progress based on actual workflow state
            const isProvisioning = !!task.provisioningStage && task.provisioningStage !== 'pending'
            const isProvisioned  = task.provisioningStage === 'provisioned'
            const isInstallation = !!task.installationPhase && task.installationPhase !== 'pending'
            const isComplete     = task.installationPhase === 'completed' || task.status === 'closed'

            // Enquiry section: done when feasibility approved, active when site_visit/work_in_progress/pending_approval
            const enquiryDone   = !!task.feasibilityApproved || isProvisioning
            const enquiryActive = !enquiryDone

            // Feasibility section: done when feasibility approved, active when site_visit/work_in_progress/pending_approval
            const feasibilityDone   = !!task.feasibilityApproved || isProvisioning
            const feasibilityActive = (task.status === 'site_visit' || task.status === 'work_in_progress' || task.status === 'pending_approval') && !feasibilityDone

            // Provisioning section: done when provisioned, active when ready/feasibility-approved or underway
            const provDone   = isProvisioned
            const provActive = !!task.feasibilityApproved && !isProvisioned

            // Installation section: done when complete, active when installation is underway OR provisioning is done (ready to start)
            const instDone   = isComplete
            const instActive = (isInstallation && !isComplete) || (isProvisioned && !isComplete)

            type WFTab = 'enquiry' | 'feasibility' | 'provisioning' | 'installation'
            const sections: { key: WFTab; label: string; sub: string; done: boolean; active: boolean; colors: { bg: string; border: string; dot: string; text: string; badge: string; badgeText: string; iconBg: string } }[] = [
              {
                key: 'enquiry', label: 'Enquiry', sub: 'Docket & Verification',
                done: enquiryDone, active: enquiryActive,
                colors: { bg: 'bg-blue-50', border: 'border-blue-200', dot: '#2563eb', text: 'text-blue-700', badge: 'bg-blue-600', badgeText: 'text-white', iconBg: 'bg-blue-600' }
              },
              {
                key: 'feasibility', label: 'Feasibility', sub: 'Site Study & Approval',
                done: feasibilityDone, active: feasibilityActive,
                colors: { bg: 'bg-violet-50', border: 'border-violet-200', dot: '#7c3aed', text: 'text-violet-700', badge: 'bg-violet-600', badgeText: 'text-white', iconBg: 'bg-violet-600' }
              },
              {
                key: 'provisioning', label: 'Provisioning', sub: 'Plan & Onboarding',
                done: provDone, active: provActive,
                colors: { bg: 'bg-orange-50', border: 'border-orange-200', dot: '#ea580c', text: 'text-orange-700', badge: 'bg-orange-500', badgeText: 'text-white', iconBg: 'bg-orange-500' }
              },
              {
                key: 'installation', label: 'Installation', sub: 'Fibre Lay & Testing',
                done: instDone, active: instActive,
                colors: { bg: 'bg-emerald-50', border: 'border-emerald-200', dot: '#059669', text: 'text-emerald-700', badge: 'bg-emerald-600', badgeText: 'text-white', iconBg: 'bg-emerald-600' }
              },
            ]

            return (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {sections.map((sec) => {
                  const isSelected = workflowTab === sec.key
                  const isLocked = !sec.active && !sec.done
                  return (
                    <button key={sec.key}
                      disabled={isLocked}
                      onClick={() => setWorkflowTab(sec.key)}
                      className={`relative rounded-2xl border-2 p-2 sm:p-3 text-left transition-all ${
                        isLocked
                          ? 'cursor-not-allowed opacity-50 bg-white border-gray-100'
                          : 'active:scale-[0.97] hover:scale-[1.02]'
                      } ${
                        isSelected && !isLocked
                          ? `${sec.colors.bg} ${sec.colors.border} shadow-md`
                          : sec.done
                            ? 'bg-white border-emerald-200 shadow-sm'
                            : sec.active
                              ? 'bg-white border-gray-200 shadow-sm'
                              : ''
                      }`}>
                      {/* Top: icon + status badge */}
                      <div className="flex items-center justify-between mb-1.5 sm:mb-2.5">
                        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center text-white text-sm shrink-0 ${
                          sec.done ? 'bg-emerald-500' : sec.active ? sec.colors.iconBg : 'bg-gray-200'
                        }`}>
                          {sec.done
                            ? <CheckCircle2 size={13} className="text-white sm:hidden" />
                            : sec.key === 'enquiry'      ? <ClipboardList size={12} className="text-white sm:hidden" />
                            : sec.key === 'feasibility'  ? <Zap size={12} className="text-white sm:hidden" />
                            : sec.key === 'provisioning' ? <Package size={12} className="text-white sm:hidden" />
                            : <Wifi size={12} className="text-white sm:hidden" />
                          }
                          {sec.done
                            ? <CheckCircle2 size={15} className="text-white hidden sm:block" />
                            : sec.key === 'enquiry'      ? <ClipboardList size={14} className="text-white hidden sm:block" />
                            : sec.key === 'feasibility'  ? <Zap size={14} className="text-white hidden sm:block" />
                            : sec.key === 'provisioning' ? <Package size={14} className="text-white hidden sm:block" />
                            : <Wifi size={14} className="text-white hidden sm:block" />
                          }
                        </div>
                        <span className={`text-[7px] sm:text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-wide ${
                          sec.done   ? 'bg-emerald-100 text-emerald-700' :
                          sec.active ? `${sec.colors.badge} ${sec.colors.badgeText}` :
                          'bg-gray-100 text-gray-400'
                        }`}>
                          {sec.done ? 'Done' : sec.active ? 'Active' : 'Locked'}
                        </span>
                      </div>
                      {/* Labels */}
                      <p className={`text-[11px] sm:text-xs font-black leading-tight ${
                        isSelected && !isLocked ? sec.colors.text : sec.done ? 'text-emerald-700' : sec.active ? 'text-gray-800' : 'text-gray-400'
                      }`}>{sec.label}</p>
                      <p className="text-[8px] sm:text-[9px] text-gray-400 font-semibold mt-0.5 leading-tight hidden sm:block">{sec.sub}</p>
                      {/* Selected underline indicator */}
                      {isSelected && !isLocked && (
                        <div className={`absolute bottom-0 left-0 right-0 h-[3px] rounded-b-2xl ${sec.colors.iconBg}`} />
                      )}
                    </button>
                  )
                })}
              </div>
            )
          })()}

          {/* ── ENQUIRY TAB CONTENT ── */}
          {workflowTab === 'enquiry' && (
            <>
              {/* ── Mismatch Banner ── */}
              {task.mismatchFlag && (
                <div className="flex items-start gap-3 p-4 rounded-2xl bg-red-50 border border-red-200">
                  <AlertTriangle size={16} className="text-red-600 shrink-0 mt-0.5 animate-bounce" />
                  <div>
                    <p className="text-sm font-black text-red-700">Details Mismatch Flagged</p>
                    <p className="text-xs text-red-600 mt-1 leading-relaxed">"{task.mismatchComments}"</p>
                    <p className="text-[10px] text-red-400 mt-1.5">Please correct the details and clear this mismatch.</p>
                  </div>
                </div>
              )}

              {/* ── Customer Details ── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50 bg-gray-50/50">
              <div className="flex items-center gap-2">
                <User size={13} className="text-gray-400" />
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Customer Details</p>
              </div>
              {isAdmin && !editingDetails && (
                <button onClick={handleStartEdit}
                  className="flex items-center gap-1.5 text-[11px] font-bold text-blue-600 hover:text-blue-800 px-2.5 py-1 rounded-lg hover:bg-blue-50 transition-colors">
                  <Pencil size={11} /> Edit
                </button>
              )}
              {isAdmin && editingDetails && (
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setEditingDetails(false)} disabled={editSaving}
                    className="text-[11px] font-bold text-gray-500 hover:text-gray-700 px-2.5 py-1 rounded-lg hover:bg-gray-100 transition-colors">
                    Cancel
                  </button>
                  <button onClick={handleSaveDetails} disabled={editSaving}
                    className="flex items-center gap-1 text-[11px] font-bold text-white px-3 py-1 rounded-lg transition-all disabled:opacity-60"
                    style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}>
                    {editSaving ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
                    {editSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </div>

            {!editingDetails ? (
              <div className="p-4 space-y-3">
                {/* ── Customer Identity ── */}
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-sm font-black shrink-0"
                    style={{ background: 'linear-gradient(135deg,#2563eb,#06b6d4)', boxShadow: '0 4px 10px rgba(37,99,235,0.25)' }}>
                    {task.customerName.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-800 truncate">{task.customerName}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Customer</p>
                  </div>
                </div>

                {/* ── Contact ── */}
                <div className="grid grid-cols-2 gap-2">
                  <DetailChip icon={<Phone size={12} className="text-blue-500" />} label="Phone" value={`+91 ${task.customerPhone}`} />
                  {task.customerEmail && <DetailChip icon={<Mail size={12} className="text-indigo-500" />} label="Email" value={task.customerEmail} />}
                  <DetailChip icon={<MapPin size={12} className="text-emerald-500" />} label="City" value={task.city} />
                  {task.location && <DetailChip icon={<Building2 size={12} className="text-amber-500" />} label="Building / Location" value={task.location} />}
                </div>

                {/* ── Address ── */}
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin size={12} className="text-red-500" />
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wide">Address</p>
                  </div>
                  <p className="text-xs font-semibold text-gray-800 leading-relaxed">{task.address}</p>
                </div>

                {/* ── Service Details ── (hidden during enquiry/feasibility) */}
                {!(task.type === 'enquiry' && (!task.provisioningStage || task.provisioningStage === 'pending')) && (task.serviceNumber || task.exchangeCode || task.bbUserId || task.oltIp || task.vlan !== undefined || task.port !== undefined || task.voipPassword !== undefined || task.billingAccountNo !== undefined || task.category || task.subServiceType) && (
                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
                      <Server size={13} className="text-blue-600" />
                      <p className="text-[10px] font-black text-blue-800 uppercase tracking-widest">Service Details</p>
                    </div>
                    <div className="p-3 grid grid-cols-2 gap-2">
                      {task.serviceNumber && <DetailChip icon={<Hash size={11} className="text-blue-500" />} label="Service Number" value={task.serviceNumber} />}
                      {task.exchangeCode && <DetailChip icon={<Radio size={11} className="text-violet-500" />} label="Exchange" value={task.exchangeCode} />}
                      {task.category && <DetailChip icon={<User size={11} className="text-gray-500" />} label="Category" value={task.category} />}
                      {task.subServiceType && <DetailChip icon={<Layers size={11} className="text-teal-500" />} label="Sub Service" value={task.subServiceType} />}
                      {task.bbUserId && <DetailChip icon={<UserCheck size={11} className="text-indigo-500" />} label="BB User ID" value={task.bbUserId} />}
                      {task.oltIp && <DetailChip icon={<Network size={11} className="text-cyan-500" />} label="OLT IP" value={task.oltIp} />}
                      {task.vlan !== undefined && task.vlan !== null && <DetailChip icon={<Hash size={11} className="text-pink-500" />} label="VLAN" value={String(task.vlan)} />}
                      {task.port !== undefined && task.port !== null && <DetailChip icon={<Hash size={11} className="text-orange-500" />} label="Port" value={String(task.port)} />}
                      {task.voipPassword !== undefined && task.voipPassword !== null && <DetailChip icon={<Key size={11} className="text-rose-500" />} label="VoIP Password" value={task.voipPassword} />}
                      {task.billingAccountNo !== undefined && <DetailChip icon={<CreditCard size={11} className="text-emerald-500" />} label="Billing A/C" value={String(task.billingAccountNo)} />}
                    </div>
                  </div>
                )}

                {/* ── Plan Details ── (hidden during enquiry/feasibility) */}
                {!(task.type === 'enquiry' && (!task.provisioningStage || task.provisioningStage === 'pending')) && (task.subscriptionPlan || task.planPeriod || task.fmc || task.provisioningPlan || task.provisioningFmc) && (
                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
                      <Package size={13} className="text-amber-600" />
                      <p className="text-[10px] font-black text-amber-800 uppercase tracking-widest">Plan & Billing</p>
                    </div>
                    <div className="p-3 grid grid-cols-2 gap-2">
                      {task.subscriptionPlan && <DetailChip icon={<Package size={11} className="text-amber-500" />} label="Plan" value={task.subscriptionPlan} />}
                      {task.provisioningPlan && <DetailChip icon={<Package size={11} className="text-amber-500" />} label="Provisioning Plan" value={task.provisioningPlan} />}
                      {task.planPeriod && <DetailChip icon={<CalendarDays size={11} className="text-orange-500" />} label="Period" value={task.planPeriod} />}
                      {task.fmc !== undefined && <DetailChip icon={<CreditCard size={11} className="text-emerald-500" />} label="FMC" value={`₹${task.fmc}`} />}
                      {task.provisioningFmc !== undefined && <DetailChip icon={<CreditCard size={11} className="text-emerald-500" />} label="Provisioning FMC" value={`₹${task.provisioningFmc}`} />}
                      {task.activationDate && <DetailChip icon={<CalendarDays size={11} className="text-blue-500" />} label="Activation Date" value={task.activationDate} />}
                    </div>
                  </div>
                )}

                {/* ── Provisioned Customer Details ── */}
                {(task.provisionedName || task.provisionedPhone) && (
                  <div className="rounded-xl border border-emerald-200 overflow-hidden">
                    <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
                      <CheckCircle2 size={13} className="text-emerald-600" />
                      <p className="text-[10px] font-black text-emerald-800 uppercase tracking-widest">Provisioned Customer</p>
                    </div>
                    <div className="p-3 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        {task.provisionedName && <DetailChip icon={<User size={11} className="text-emerald-600" />} label="Name" value={task.provisionedName} />}
                        {task.provisionedPhone && <DetailChip icon={<Phone size={11} className="text-emerald-600" />} label="Phone" value={`+91 ${task.provisionedPhone}`} />}
                        {task.provisionedEmail && <DetailChip icon={<Mail size={11} className="text-emerald-600" />} label="Email" value={task.provisionedEmail} />}
                        {task.provisionedCity && <DetailChip icon={<MapPin size={11} className="text-emerald-600" />} label="City" value={task.provisionedCity} />}
                        {task.provisionedLocation && <DetailChip icon={<Building2 size={11} className="text-emerald-600" />} label="Location" value={task.provisionedLocation} />}
                      </div>
                      {task.provisionedAddress && (
                        <div className="bg-emerald-50/40 rounded-lg p-2.5 border border-emerald-100">
                          <p className="text-[9px] font-black text-emerald-600 uppercase tracking-wide mb-0.5">Address</p>
                          <p className="text-xs font-semibold text-gray-800">{task.provisionedAddress}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Installation Engineer ── */}
                {task.installationEngineerName && (
                  <div className="rounded-xl border border-violet-200 overflow-hidden">
                    <div className="px-4 py-2 bg-violet-50 border-b border-violet-100 flex items-center gap-2">
                      <Wifi size={13} className="text-violet-600" />
                      <p className="text-[10px] font-black text-violet-800 uppercase tracking-widest">Installation</p>
                    </div>
                    <div className="p-3">
                      <DetailChip icon={<UserCheck size={11} className="text-violet-600" />} label="Assigned Engineer" value={task.installationEngineerName} />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Customer Name <span className="text-red-400">*</span></label>
                    <input value={editName} onChange={e => setEditName(e.target.value)}
                      className="w-full text-sm font-semibold text-gray-800 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                      placeholder="Customer name" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Phone <span className="text-red-400">*</span></label>
                    <input value={editPhone} onChange={e => setEditPhone(e.target.value)}
                      className="w-full text-sm font-semibold text-gray-800 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                      placeholder="10-digit mobile" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Email</label>
                    <input value={editEmail} onChange={e => setEditEmail(e.target.value)}
                      className="w-full text-sm font-semibold text-gray-800 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                      placeholder="email@example.com" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">City <span className="text-red-400">*</span></label>
                    <input value={editCity} onChange={e => setEditCity(e.target.value)}
                      className="w-full text-sm font-semibold text-gray-800 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                      placeholder="City" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Address <span className="text-red-400">*</span></label>
                  <textarea value={editAddress} onChange={e => setEditAddress(e.target.value)} rows={2}
                    className="w-full text-sm font-semibold text-gray-800 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all resize-none"
                    placeholder="Full address" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Building / Landmark</label>
                  <input value={editLocation} onChange={e => setEditLocation(e.target.value)}
                    className="w-full text-sm font-semibold text-gray-800 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                    placeholder="Building name, landmark, etc." />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Description</label>
                  <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} rows={3}
                    className="w-full text-sm text-gray-800 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all resize-none"
                    placeholder="Task description" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Notes</label>
                  <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={2}
                    className="w-full text-sm text-gray-800 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all resize-none"
                    placeholder="Internal notes" />
                </div>
              </div>
            )}
          </div>

          {/* Verification Checklist (engineer) */}
          {!isDone && (() => {
            const tickedCount = fieldsToVerify.filter(f => tickedFields[f.key]).length
            const allDone = tickedCount === fieldsToVerify.length && fieldsToVerify.length > 0
            const inProvisioningPhase = !!(task.provisioningStage && task.provisioningStage !== 'pending')
            const feasibilityDone = !!task.feasibilityStatus
            const readOnly = feasibilityDone
            return (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Single-row summary — always visible */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${allDone ? 'bg-emerald-500' : 'bg-gray-100'}`}>
                    {allDone
                      ? <CheckCircle2 size={13} className="text-white" />
                      : <ClipboardList size={13} className="text-gray-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-black text-gray-700">Enquiry Verification Checklist</p>
                    <p className={`text-[10px] font-semibold mt-0.5 ${allDone ? 'text-emerald-500' : 'text-gray-400'}`}>
                      {tickedCount}/{fieldsToVerify.length} verified{allDone ? ' · All done' : ''}
                    </p>
                  </div>
                  {readOnly && <span className="text-[9px] font-black text-gray-400 uppercase tracking-wide px-2 py-0.5 bg-gray-100 rounded-full">Locked</span>}
                  <button onClick={() => setChecklistExpanded(v => !v)}
                    className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${checklistExpanded ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                    title={checklistExpanded ? 'Hide checklist' : 'View checklist'}>
                    <Eye size={13} />
                  </button>
                </div>

                {/* Expandable detail */}
                {checklistExpanded && (
                  <div className="px-4 pb-4 space-y-2 border-t border-gray-50 pt-3">
                    <div className="space-y-1.5">
                      {fieldsToVerify.map(field => (
                        <label key={field.key} className={`flex items-start gap-3 p-2.5 rounded-xl border transition-all ${readOnly ? 'bg-slate-50 border-slate-100/60 cursor-not-allowed opacity-75' : 'bg-slate-50/80 hover:bg-slate-50 border-slate-100/60 cursor-pointer'}`}>
                          <input type="checkbox" checked={!!tickedFields[field.key]} onChange={() => handleToggleTick(field.key)}
                            disabled={readOnly}
                            className={`mt-0.5 rounded h-4 w-4 shrink-0 ${readOnly ? 'border-slate-300 bg-slate-100 cursor-not-allowed' : 'border-slate-300 text-blue-600 focus:ring-blue-500'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-wide">{field.label}</p>
                            <p className={`text-xs font-bold ${tickedFields[field.key] ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{field.value}</p>
                          </div>
                          {tickedFields[field.key] && <CheckCircle2 size={13} className="text-emerald-500 shrink-0 self-center" />}
                        </label>
                      ))}
                    </div>
                    {/* Mismatch — only shown when NOT in provisioning phase AND NOT read-only */}
                    {!inProvisioningPhase && !readOnly && (
                      <div className="pt-2 border-t border-gray-100">
                        {!mismatchOpen ? (
                          <button onClick={() => setMismatchOpen(true)}
                            className="w-full py-2.5 text-xs font-black text-red-500 bg-red-50 hover:bg-red-100/60 rounded-xl flex items-center justify-center gap-1.5 transition-all">
                            <AlertTriangle size={13} /> Report Mismatch
                          </button>
                        ) : (
                          <div className="space-y-2">
                            <textarea value={mismatchComments} onChange={e => setMismatchComments(e.target.value)}
                              placeholder="Specify which fields are incorrect and the correct details…"
                              rows={3} className="w-full text-xs text-gray-700 placeholder-gray-400 border border-gray-200 rounded-xl p-2.5 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 resize-none transition-all" />
                            <div className="flex gap-2">
                              <button onClick={handleReportMismatch} disabled={reportingMismatch}
                                className="flex-1 py-2 text-white font-black rounded-xl text-xs bg-red-500 hover:bg-red-600 flex items-center justify-center gap-1.5 disabled:opacity-50">
                                {reportingMismatch && <Loader2 size={12} className="animate-spin" />} Submit Report
                              </button>
                              <button onClick={() => setMismatchOpen(false)} className="px-3 py-2 bg-slate-100 text-slate-600 font-bold rounded-xl text-xs">Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })()}


          {/* ── BSNL Connection Parameters ── */}
          {(task.bbUserId || task.serviceNumber || task.oltIp || task.subscriptionPlan) && (
            <Section title="BSNL Connection Parameters" icon={Activity}>
              <div className="grid grid-cols-2 gap-3">
                {task.bbUserId && (
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">Broadband User ID</p>
                    <p className="text-xs font-bold text-gray-800 font-mono select-all">{task.bbUserId}</p>
                  </div>
                )}
                {task.serviceNumber && (
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">Service Number</p>
                    <p className="text-xs font-bold text-gray-800">{task.serviceNumber}</p>
                  </div>
                )}
                {task.oltIp && (
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">OLT IP Address</p>
                    <p className="text-xs font-bold font-mono text-gray-800 select-all">{task.oltIp}</p>
                  </div>
                )}
                {(task.vlan !== null && task.vlan !== undefined) && (
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">VLAN / Port</p>
                    <p className="text-xs font-bold font-mono text-indigo-700">{task.vlan} / {task.port ?? '—'}</p>
                  </div>
                )}
                {task.exchangeCode && (
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">Exchange Code</p>
                    <p className="text-xs font-semibold text-gray-700">{task.exchangeCode}</p>
                  </div>
                )}
                {task.category && (
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">Category</p>
                    <p className="text-xs font-semibold text-gray-700">{task.category}</p>
                  </div>
                )}
                {task.subscriptionPlan && (
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 col-span-2">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">Subscription Plan</p>
                    <p className="text-xs font-bold text-gray-900">{task.subscriptionPlan}{task.planPeriod ? ` · ${task.planPeriod}` : ''}</p>
                  </div>
                )}
                {task.billingAccountNo && (
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">Billing Account</p>
                    <p className="text-xs font-mono font-semibold text-gray-700">{task.billingAccountNo}</p>
                  </div>
                )}
                {task.fmc !== undefined && (
                  <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                    <p className="text-[9px] font-black text-emerald-500 uppercase tracking-wider mb-1">Monthly FMC</p>
                    <p className="text-sm font-extrabold text-emerald-700">₹ {task.fmc}</p>
                  </div>
                )}
                {task.voipPassword && (
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">VoIP Password</p>
                    <p className="text-xs font-mono font-semibold text-gray-700">{task.voipPassword}</p>
                  </div>
                )}
                {task.activationDate && (
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">Activation Date</p>
                    <p className="text-xs font-semibold text-gray-700">{task.activationDate}</p>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* ── Speed Test (admin view) ── */}
          {task.speedTestDownload && (
            <Section title="Modem Speed Test" icon={Gauge}>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                  { label: 'Download', value: `${task.speedTestDownload} Mbps`, color: 'text-blue-600' },
                  { label: 'Upload', value: `${task.speedTestUpload} Mbps`, color: 'text-emerald-600' },
                  { label: 'Ping', value: `${task.speedTestPing} ms`, color: 'text-indigo-600' },
                ].map(m => (
                  <div key={m.label} className="bg-slate-50 rounded-xl p-3 border border-slate-100 text-center">
                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">{m.label}</p>
                    <p className={`text-base font-extrabold mt-1 ${m.color}`}>{m.value}</p>
                  </div>
                ))}
              </div>
              {task.speedTestIsp && <p className="text-xs text-gray-500">ISP: <span className="font-bold text-gray-700">{task.speedTestIsp}</span></p>}
              {task.speedTestIp && <p className="text-xs text-gray-500">IP: <span className="font-mono text-gray-600">{task.speedTestIp}</span></p>}
            </Section>
          )}

          {/* ── Feasibility Review Banner (admin, work_in_progress) ── */}
          {task.type === 'enquiry' && task.status === 'work_in_progress' && task.feasibilityStatus && isAdmin && (
            <div className={`flex items-start gap-3 p-4 rounded-2xl border-2 ${
              task.feasibilityStatus === 'feasible'
                ? 'bg-emerald-50 border-emerald-300'
                : 'bg-red-50 border-red-300'
            }`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                task.feasibilityStatus === 'feasible' ? 'bg-emerald-500' : 'bg-red-500'
              }`}>
                {task.feasibilityStatus === 'feasible'
                  ? <CheckCircle2 size={16} className="text-white" />
                  : <XCircle size={16} className="text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-black ${task.feasibilityStatus === 'feasible' ? 'text-emerald-800' : 'text-red-800'}`}>
                  Feasibility Study Submitted — {task.feasibilityStatus === 'feasible' ? 'Site is Feasible ⚡' : 'Site Not Feasible ❌'}
                </p>
                <p className={`text-[11px] mt-0.5 leading-relaxed ${task.feasibilityStatus === 'feasible' ? 'text-emerald-700' : 'text-red-700'}`}>
                  {task.feasibilityStatus === 'feasible'
                    ? 'Review the study details in the Feasibility tab, then use the sidebar to Mark Enquiry Complete and choose the next step.'
                    : 'Site is not feasible. Review comments in the Feasibility tab, then close the docket.'}
                </p>
              </div>
            </div>
          )}
            </>
          )}

          {/* ── Provisioning Workflow ── */}
          {workflowTab === 'provisioning' && (
            <>
              {/* Compact Customer Summary */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50 bg-gray-50/50">
                  <User size={13} className="text-gray-400" />
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Customer</p>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-xs font-black shrink-0"
                      style={{ background: 'linear-gradient(135deg,#2563eb,#06b6d4)' }}>
                      {task.customerName.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-800 truncate">{task.customerName}</p>
                      <p className="text-[10px] text-gray-400">{task.city}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                      <Phone size={11} className="text-blue-500 shrink-0" />
                      <p className="text-xs font-bold text-gray-800 font-mono">+91 {task.customerPhone}</p>
                    </div>
                    {task.customerEmail && (
                      <div className="flex items-center gap-2">
                        <Mail size={11} className="text-indigo-500 shrink-0" />
                        <p className="text-xs font-bold text-gray-800 truncate">{task.customerEmail}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {renderProvisioningSection()}
            </>
          )}

          {/* ── Installation Workflow ── */}
          {workflowTab === 'installation' && renderInstallationSection()}

          {/* ── Site Photos ── */}
          {(savedUrls.length > 0 || isSiteVisit) && (
            <Section title={`Site Photos${savedUrls.length ? ` · ${savedUrls.length}` : ''}`} icon={Camera}>
              {savedUrls.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {savedUrls.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                      className="w-20 h-20 rounded-xl overflow-hidden border border-gray-200 hover:opacity-80 transition-opacity shrink-0">
                      <img src={url} alt={`site-${i}`} className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
              {isSiteVisit && (
                <div className="space-y-3">
                  {pendingPhotos.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {pendingPhotos.map((ph, i) => (
                        <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-blue-200 shrink-0">
                          <img src={ph.preview} alt="" className="w-full h-full object-cover" />
                          {uploadProgress[i] !== undefined && uploadProgress[i] < 100 && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <span className="text-white text-[10px] font-black">{uploadProgress[i]}%</span>
                            </div>
                          )}
                          {!uploading && (
                            <button onClick={() => setPendingPhotos(prev => { URL.revokeObjectURL(prev[i].preview); return prev.filter((_,j) => j!==i) })}
                              className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                              <Trash2 size={9} className="text-white" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <label className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl cursor-pointer text-xs font-black text-gray-600 transition-colors">
                      <ImagePlus size={13} /> Add Photos
                      <input type="file" multiple accept="image/*" className="hidden" onChange={handlePickPhotos} />
                    </label>
                    {pendingPhotos.length > 0 && (
                      <button onClick={handleUploadPhotos} disabled={uploading}
                        className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-black text-white transition-all disabled:opacity-60"
                        style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                        {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                        {uploading ? 'Uploading…' : `Upload ${pendingPhotos.length}`}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* ── Customer Feedback (closed) ── */}
          {task.status === 'closed' && task.customerRating && (
            <Section title="Customer Feedback" icon={Star}>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} className={`text-xl ${i < task.customerRating! ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
                  ))}
                </div>
                <span className="text-sm font-black text-gray-700">{['','Poor','Fair','Good','Very Good','Excellent'][task.customerRating]}</span>
              </div>
              {task.feedbackCategories && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {(['serviceQuality','responseTime','communication','workmanship'] as const).map(k => (
                    <div key={k} className="bg-gray-50 rounded-xl p-3">
                      <p className="text-[9px] text-gray-400 font-bold capitalize mb-1">{k.replace(/([A-Z])/g,' $1')}</p>
                      <div className="flex gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <span key={i} className={`text-sm ${i < (task.feedbackCategories?.[k] ?? 0) ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {task.wouldRecommend !== undefined && (
                <span className={`inline-flex items-center text-[11px] font-black px-3 py-1.5 rounded-full ${task.wouldRecommend ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                  {task.wouldRecommend ? '👍 Would Recommend' : '👎 Would Not Recommend'}
                </span>
              )}
              {task.customerFeedback && <p className="text-sm text-gray-600 italic mt-3 leading-relaxed">"{task.customerFeedback}"</p>}
            </Section>
          )}

          {/* ── FEASIBILITY TAB CONTENT ── */}
          {workflowTab === 'feasibility' && (
            <>
              {/* ── Feasibility Summary ── */}
              {task.type === 'enquiry' && task.feasibilityStatus && (
                <div className="rounded-2xl overflow-hidden border shadow-sm"
                  style={{ borderColor: task.feasibilityStatus === 'feasible' ? '#10b981' : '#ef4444', borderLeftWidth: 4 }}>
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <Zap size={13} className="text-indigo-500" />
                      <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">Feasibility Study</p>
                    </div>
                    <span className={`text-[10px] font-black px-2.5 py-1 rounded-full ${task.feasibilityStatus === 'feasible' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                      {task.feasibilityStatus === 'feasible' ? '⚡ FEASIBLE' : '❌ NOT FEASIBLE'}
                    </span>
                  </div>
                  <div className="p-4 bg-white space-y-3">
                    {task.feasibilityComments && <p className="text-sm text-gray-700 leading-relaxed">{task.feasibilityComments}</p>}
                    {task.feasibilityWorkflowRequired?.length ? (
                      <div className="flex flex-wrap gap-1.5">
                        {task.feasibilityWorkflowRequired.map(wf => (
                          <span key={wf} className="text-[10px] bg-slate-100 text-slate-700 border border-gray-200/50 px-2.5 py-0.5 rounded-lg font-bold">{wf}</span>
                        ))}
                      </div>
                    ) : null}
                    {task.feasibilityLatitude !== undefined && (
                      <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                        <p className="text-xs font-mono text-gray-600">{task.feasibilityLatitude?.toFixed(6)}, {task.feasibilityLongitude?.toFixed(6)}</p>
                        <a href={`https://www.google.com/maps/search/?api=1&query=${task.feasibilityLatitude},${task.feasibilityLongitude}`}
                          target="_blank" rel="noreferrer"
                          className="text-[10px] font-black text-indigo-600 bg-indigo-50 border border-indigo-100 px-2.5 py-1.5 rounded-xl flex items-center gap-1.5">
                          <MapPin size={11} /> Map
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Feasibility Submit (engineer, enquiry site_visit) ── */}
              {(isEnquirySiteVisit || isEnquiryInProgress) && !isDone && !task.feasibilityApproved && (
                <Section title="Feasibility Study" icon={Zap}>
                  {!feasOpen ? (
                    <button onClick={() => setFeasOpen(true)}
                      className="w-full py-3 rounded-xl font-black text-sm text-white flex items-center justify-center gap-2 transition-all"
                      style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', boxShadow: '0 4px 14px rgba(99,102,241,0.35)' }}>
                      <Zap size={15} /> Submit Feasibility Study
                    </button>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex gap-3">
                        {(['feasible','not_feasible'] as const).map(opt => (
                          <button key={opt} onClick={() => setFeasStatus(opt)}
                            className={`flex-1 py-3 rounded-xl font-black text-sm border-2 transition-all ${feasStatus === opt ? (opt === 'feasible' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-red-500 text-white border-red-500') : 'bg-white text-gray-500 border-gray-200'}`}>
                            {opt === 'feasible' ? '⚡ Feasible' : '❌ Not Feasible'}
                          </button>
                        ))}
                      </div>
                      {locError && <p className="text-xs text-red-500 bg-red-50 p-2 rounded-lg">{locError}</p>}
                      {fetchingLoc && <p className="text-xs text-blue-500 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" />Acquiring GPS…</p>}
                      {feasLat !== undefined && <p className="text-xs text-emerald-600 font-mono">📍 {feasLat.toFixed(5)}, {feasLng?.toFixed(5)}</p>}
                      <textarea value={feasComments} onChange={e => setFeasComments(e.target.value)}
                        placeholder="Additional comments or observations…" rows={3}
                        className="w-full text-sm text-gray-700 placeholder-gray-400 border border-gray-200 rounded-xl p-3 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none" />
                      <div className="flex gap-2">
                        <button onClick={handleFeasibilitySubmit} disabled={feasSubmitting || fetchingLoc}
                          className="flex-1 py-2.5 text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                          style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                          {feasSubmitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Submit
                        </button>
                        <button onClick={() => setFeasOpen(false)} className="px-4 py-2.5 bg-gray-100 text-gray-600 font-bold rounded-xl text-sm">Cancel</button>
                      </div>
                    </div>
                  )}
                </Section>
              )}
            </>
          )}

          {/* ── Feedback Form (pending_feedback) ── */}
          {task.status === 'pending_feedback' && !isDone && (
            <Section title="Customer Feedback" icon={Star}>
              {!feedbackOpen ? (
                <button onClick={() => setFeedbackOpen(true)}
                  className="w-full py-3 rounded-xl font-black text-sm text-white flex items-center justify-center gap-2 transition-all"
                  style={{ background: 'linear-gradient(135deg,#f59e0b,#f97316)', boxShadow: '0 4px 14px rgba(245,158,11,0.35)' }}>
                  <Star size={15} /> Collect Customer Feedback
                </button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-black text-gray-500 uppercase tracking-widest mb-2">Overall Rating</p>
                    <div className="flex gap-2">
                      {[1,2,3,4,5].map(r => (
                        <button key={r} onClick={() => setRating(r)}
                          className={`text-2xl transition-transform hover:scale-110 ${r <= rating ? 'text-amber-400' : 'text-gray-200'}`}>★</button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {(['serviceQuality','responseTime','communication','workmanship'] as const).map(k => (
                      <div key={k} className="bg-gray-50 rounded-xl p-2">
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-wide mb-1.5 capitalize">{k.replace(/([A-Z])/g,' $1')}</p>
                        <div className="flex gap-1">
                          {[1,2,3,4,5].map(r => (
                            <button key={r} onClick={() => setCatRatings(prev => ({...prev, [k]: r}))}
                              className={`text-sm ${r <= catRatings[k] ? 'text-amber-400' : 'text-gray-200'}`}>★</button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-xs font-black text-gray-500 uppercase tracking-widest mb-2">Would Recommend?</p>
                    <div className="flex gap-2">
                      <button onClick={() => setWouldRecommend(true)} className={`flex-1 py-2.5 rounded-xl font-black text-sm border-2 transition-all ${wouldRecommend === true ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-500 border-gray-200'}`}>👍 Yes</button>
                      <button onClick={() => setWouldRecommend(false)} className={`flex-1 py-2.5 rounded-xl font-black text-sm border-2 transition-all ${wouldRecommend === false ? 'bg-red-500 text-white border-red-500' : 'bg-white text-gray-500 border-gray-200'}`}>👎 No</button>
                    </div>
                  </div>
                  <textarea value={feedbackText} onChange={e => setFeedbackText(e.target.value)}
                    placeholder="Customer comments (optional)…" rows={2}
                    className="w-full text-sm text-gray-700 placeholder-gray-400 border border-gray-200 rounded-xl p-3 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 resize-none" />
                  <button onClick={handleFeedbackSubmit} disabled={submitting}
                    className="w-full py-3 text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ background: 'linear-gradient(135deg,#f59e0b,#f97316)', boxShadow: '0 4px 14px rgba(245,158,11,0.35)' }}>
                    {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Submit & Close Task
                  </button>
                </div>
              )}
            </Section>
          )}
          {/* ── Details / Comments / History Tabs ── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Tab bar */}
            <div className="flex border-b border-gray-100">
              {([
                { key: 'details',  label: 'Details',  icon: <ClipboardList size={12} /> },
                { key: 'comments', label: 'Comments', icon: <MessageSquare size={12} />, badge: comments.length || undefined },
                { key: 'history',  label: 'History',  icon: <History size={12} /> },
              ] as const).map(tab => (
                <button key={tab.key} onClick={() => setDetailTab(tab.key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-[11px] font-black transition-all border-b-2 ${
                    detailTab === tab.key
                      ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                      : 'border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                  }`}>
                  {tab.icon}
                  {tab.label}
                  {'badge' in tab && tab.badge ? (
                    <span className="text-[9px] font-black bg-blue-100 text-blue-600 rounded-full px-1.5 py-0.5 leading-none">{tab.badge}</span>
                  ) : null}
                </button>
              ))}
            </div>

            {/* Details tab */}
            {detailTab === 'details' && (
              <div className="p-4 space-y-3">
                {task.description ? (
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Description</p>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{task.description}</p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 text-center py-4">No description added.</p>
                )}
                {task.notes && (
                  <div className="pt-3 border-t border-gray-50">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Notes</p>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{task.notes}</p>
                  </div>
                )}
              </div>
            )}

            {/* Comments tab */}
            {detailTab === 'comments' && (
              <div className="p-4 space-y-3">
                {commentsLoading ? (
                  <div className="flex justify-center py-8"><Loader2 size={16} className="animate-spin text-gray-300" /></div>
                ) : comments.length === 0 ? (
                  <p className="text-center text-xs text-gray-400 py-6">No comments yet. Be the first to add one.</p>
                ) : (
                  <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                    {comments.map(c => {
                      const isMine = c.authorId === engineerId
                      return (
                        <div key={c.id} className={`flex gap-2.5 ${isMine ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-[10px] font-black text-white ${
                            c.authorRole === 'admin' ? 'bg-gradient-to-br from-blue-500 to-blue-700' : 'bg-gradient-to-br from-violet-500 to-purple-700'
                          }`}>
                            {c.authorName.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()}
                          </div>
                          <div className={`max-w-[75%] ${isMine ? 'items-end' : 'items-start'} flex flex-col gap-0.5`}>
                            <div className="flex items-center gap-1.5">
                              {!isMine && <span className="text-[10px] font-black text-gray-500">{c.authorName}</span>}
                              {c.authorRole === 'admin' && <span className="text-[9px] font-black text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded-full">Admin</span>}
                              {isMine && <span className="text-[10px] font-black text-gray-500">{c.authorName}</span>}
                            </div>
                            <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                              isMine ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-tr-sm' : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                            }`}>
                              {c.text.split(/(@admin)/gi).map((part, i) =>
                                part.toLowerCase() === '@admin'
                                  ? <span key={i} className={`font-black ${isMine ? 'text-yellow-300' : 'text-blue-600'}`}>{part}</span>
                                  : part
                              )}
                            </div>
                            <span className="text-[9px] text-gray-400">
                              {c.createdAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} · {c.createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
                <div className="flex gap-2 items-end pt-2 border-t border-gray-50">
                  <div className="flex-1">
                    <textarea value={commentText} onChange={e => setCommentText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmitComment() }}
                      rows={2} placeholder={isAdmin ? 'Add a comment…' : 'Add a comment… type @admin to notify'}
                      className="w-full text-sm text-gray-800 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all resize-none" />
                    {!isAdmin && <p className="text-[10px] text-gray-400 mt-1 px-1">Tip: type <span className="font-black text-blue-500">@admin</span> to notify the admin</p>}
                  </div>
                  <button onClick={handleSubmitComment} disabled={!commentText.trim() || submittingComment}
                    className="mb-6 p-2.5 rounded-xl text-white disabled:opacity-40 transition-all active:scale-95"
                    style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}>
                    {submittingComment ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  </button>
                </div>
              </div>
            )}

            {/* History tab */}
            {detailTab === 'history' && (
              <div className="p-4">
                {timelineLoading ? (
                  <div className="flex justify-center py-8"><Loader2 size={16} className="animate-spin text-gray-300" /></div>
                ) : (
                  <div className="relative pl-6 border-l-2 border-slate-100 space-y-5 ml-3 py-2">
                    {(timelineEvents.length > 0 ? timelineEvents : [
                      { id: '__created', status: 'open' as const, title: 'Task Created', description: task.description || undefined, updatedBy: 'System', createdAt: task.createdAt },
                      ...(task.assignedTo ? [{ id: '__assigned', status: 'assigned' as const, title: 'Assigned to Engineer', description: `Assigned to ${task.assignedEngineerName || 'Field Engineer'}`, updatedBy: 'Admin', createdAt: task.updatedAt }] : []),
                      ...(task.status === 'closed' ? [{ id: '__closed', status: 'closed' as const, title: 'Task Closed', description: undefined, updatedBy: 'System', createdAt: task.closedAt ?? task.updatedAt }] : []),
                    ]).map((evt, idx) => {
                      const dotColors: Record<string, string> = {
                        closed: 'bg-emerald-500', cancelled: 'bg-gray-400', pending_approval: 'bg-amber-500',
                        pending_feedback: 'bg-purple-500', in_progress: 'bg-violet-500', site_visit: 'bg-indigo-500',
                        work_in_progress: 'bg-orange-500', assigned: 'bg-blue-500', open: 'bg-slate-400',
                      }
                      const dot = dotColors[evt.status] ?? 'bg-blue-500'
                      return (
                        <div key={evt.id || idx} className="relative">
                          <div className={`absolute -left-[31px] top-1 w-3.5 h-3.5 rounded-full ${dot} border-4 border-white shadow-sm`} />
                          <p className="text-xs font-black text-gray-800">{evt.title}</p>
                          {evt.description && <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{evt.description}</p>}
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {new Date(evt.createdAt).toLocaleString('en-IN', { hour12: true, hour: 'numeric', minute: '2-digit', day: 'numeric', month: 'short' })}
                            {' · '}{evt.updatedBy}
                          </p>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ══════════════════ RIGHT SIDEBAR ══════════════════ */}
        <div className="lg:w-80 shrink-0 space-y-4 mt-4 lg:mt-0">

          {/* ── Status & Actions ── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-50 bg-gray-50/50">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                {task.type === 'enquiry' ? 'Docket Status' : 'Status & Actions'}
              </p>
            </div>
            <div className="p-4 space-y-3">
              {/* Current status badge */}
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border ${s.bg} ${s.border}`}>
                <span className={`${s.text} flex items-center gap-1.5 font-black text-sm`}>{s.icon} {s.label}</span>
              </div>

              {/* ── ENQUIRY TYPE: custom workflow actions ── */}
              {task.type === 'enquiry' && !isDone && (() => {
                // State: open — no action needed (engineer picks it up)
                // State: assigned → in_progress → site_visit → work_in_progress → pending_approval (standard flow)
                // When work_in_progress: show "Mark Enquiry Complete" instead of generic next
                // When pending_approval: show decision panel inline here too (duplicated in main column)

                // site_visit: engineer submits feasibility via the form in the main column
                if (task.status === 'site_visit') {
                  if (!task.feasibilityStatus) {
                    return (
                      <div className="flex items-start gap-2 p-3 rounded-xl bg-indigo-50 border border-indigo-100">
                        <Zap size={13} className="text-indigo-500 shrink-0 mt-0.5" />
                        <p className="text-[11px] text-indigo-700 font-semibold leading-snug">
                          Submit the Feasibility Study below to proceed.
                        </p>
                      </div>
                    )
                  }
                  // feasibility already submitted — auto-advanced, nothing needed here
                  return null
                }

                // in_progress + provisioningStage: provisioning workflow is active — show current step in sidebar
                if (task.status === 'in_progress' && task.provisioningStage && task.provisioningStage !== 'pending') {

                  // Installation is active — show live installation phase
                  if (task.provisioningStage === 'provisioned') {
                    const instPhase = (task.installationPhase ?? 'pending') as typeof INST_PHASES[number]
                    const instPhaseLabel = INST_PHASE_LABELS[instPhase]
                    const instPhaseConfig: Record<string, { color: string; bg: string; border: string; icon: React.ReactNode; hint: string }> = {
                      pending:          { color: 'text-violet-700', bg: 'bg-violet-50', border: 'border-violet-100', icon: <Wifi size={13} className="text-violet-500 shrink-0" />, hint: 'Click Begin Installation to start the installation workflow.' },
                      assigned:         { color: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-100',   icon: <UserCheck size={13} className="text-blue-500 shrink-0" />, hint: 'Installation started — assign an engineer and load customer data.' },
                      in_progress:      { color: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-100', icon: <Activity size={13} className="text-indigo-500 shrink-0" />, hint: 'Installation is in progress.' },
                      site_visit:       { color: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-100',  icon: <MapPin size={13} className="text-amber-500 shrink-0" />, hint: 'Engineer is on-site performing installation.' },
                      work_in_progress: { color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-100', icon: <Zap size={13} className="text-orange-500 shrink-0" />, hint: 'Work is in progress at the site.' },
                      installation_done: { color: 'text-teal-700', bg: 'bg-teal-50', border: 'border-teal-100', icon: <CheckCircle2 size={13} className="text-teal-500 shrink-0" />, hint: 'Physical installation completed. Ready for speed test.' },
                      speed_test:       { color: 'text-cyan-700', bg: 'bg-cyan-50', border: 'border-cyan-100', icon: <Gauge size={13} className="text-cyan-500 shrink-0" />, hint: 'Run speed test from customer modem to verify connectivity.' },
                      pending_approval: { color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-100', icon: <ClipboardList size={13} className="text-purple-500 shrink-0" />, hint: 'Installation and speed test done — awaiting admin approval.' },
                      completed:        { color: 'text-emerald-700',bg: 'bg-emerald-50',border: 'border-emerald-100',icon: <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />, hint: 'Installation completed successfully.' },
                    }
                    const cfg = instPhaseConfig[instPhase] ?? instPhaseConfig['pending']
                    return (
                      <div className="space-y-2">
                        <div className={`flex items-center gap-2 p-3 rounded-xl border ${cfg.bg} ${cfg.border}`}>
                          {cfg.icon}
                          <div>
                            <p className={`text-[9px] font-black uppercase tracking-widest text-gray-400 mb-0.5`}>Installation</p>
                            <p className={`text-xs font-black ${cfg.color}`}>{instPhaseLabel}</p>
                            <p className="text-[10px] text-gray-500 mt-0.5 leading-snug">{cfg.hint}</p>
                          </div>
                        </div>
                        <p className="text-[10px] text-center text-gray-400">Manage in the Installation Workflow below ↓</p>
                      </div>
                    )
                  }

                  const provStageLabels: Record<string, { label: string; color: string; hint: string }> = {
                    customer_confirmed: { label: 'Contact Customer', color: 'text-blue-700', hint: 'Waiting for plan selection from customer.' },
                    plan_selected:      { label: 'Plan Selected', color: 'text-violet-700', hint: 'Plan selected — proceed with BSNL registration.' },
                    bsnl_registered:    { label: 'BSNL Onboarding', color: 'text-orange-700', hint: 'BSNL registered — enter onboarding details.' },
                  }
                  const info = provStageLabels[task.provisioningStage]
                  return info ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100">
                        <Package size={13} className="text-blue-500 shrink-0" />
                        <div>
                          <p className={`text-xs font-black ${info.color}`}>{info.label}</p>
                          <p className="text-[10px] text-gray-500 mt-0.5 leading-snug">{info.hint}</p>
                        </div>
                      </div>
                      <p className="text-[10px] text-center text-gray-400">Continue in the Provisioning Workflow below ↓</p>
                    </div>
                  ) : null
                }

                // work_in_progress: admin reviews feasibility result and marks complete
                if (task.status === 'work_in_progress' && isAdmin) {
                  if (!task.feasibilityStatus) {
                    return (
                      <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-100">
                        <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" />
                        <p className="text-[11px] text-amber-700 font-semibold leading-snug">
                          Awaiting feasibility study from the engineer.
                        </p>
                      </div>
                    )
                  }
                  return (
                    <button onClick={handleMarkEnquiryComplete} disabled={markingComplete}
                      className="w-full py-3 text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.98] disabled:opacity-60"
                      style={{ background: 'linear-gradient(135deg,#10b981,#059669)', boxShadow: '0 4px 16px rgba(16,185,129,0.3)' }}>
                      {markingComplete ? <Loader2 size={15} className="animate-spin" /> : <CheckSquare size={15} />}
                      Mark Enquiry Complete
                    </button>
                  )
                }

                if (task.status === 'pending_approval' && isAdmin) {
                  // Step 1: Admin must approve feasibility first
                  if (task.feasibilityStatus === 'feasible' && !task.feasibilityApproved) {
                    return (
                      <div className="space-y-2">
                        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest flex items-center gap-1.5">
                          <CheckCircle2 size={11} /> Feasibility Approved — Next Step
                        </p>
                        <button
                          onClick={() => {
                            // Approve feasibility only — provisioning stage stays 'pending' until admin clicks Start Provisioning
                            taskService.updateProvisioningStage(task!.id, 'pending', {
                              feasibilityApproved: true,
                            }).then(res => {
                              if (res.success) {
                                toast.success('Feasibility approved')
                                taskService.addTimelineEvent(task!.id, {
                                  status: 'pending_approval',
                                  title: 'Feasibility Approved',
                                  description: 'Feasibility study approved by admin. Ready to start provisioning.',
                                  updatedBy: user?.name || 'Admin',
                                }).then(() => { load(); loadTimeline() })
                              }
                              else toast.error(res.error ?? 'Failed')
                            })
                          }}
                          disabled={provSaving}
                          className="w-full py-3 text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-60"
                          style={{ background: 'linear-gradient(135deg,#10b981,#059669)', boxShadow: '0 4px 16px rgba(16,185,129,0.3)' }}>
                          {provSaving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                          Approve Feasibility
                        </button>
                        <p className="text-[10px] text-center text-gray-400">After approval, choose to start provisioning or close ticket</p>
                      </div>
                    )
                  }

                  // Step 2: After feasibility approved, show provisioning options
                  if (task.feasibilityApproved) {
                    return (
                      <div className="space-y-2">
                        <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-1.5">
                          <Package size={11} /> Choose Provisioning Path
                        </p>
                        <button
                          onClick={() => handleProvStage('customer_confirmed', { provisioningChecklist: [], status: 'in_progress' })}
                          disabled={provSaving}
                          className="w-full py-3 text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-60"
                          style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', boxShadow: '0 4px 16px rgba(37,99,235,0.3)' }}>
                          {provSaving ? <Loader2 size={15} className="animate-spin" /> : <ThumbsUp size={15} />}
                          Start Provisioning
                        </button>
                        <p className="text-[9px] text-center text-gray-400">— or —</p>
                        {!showCloseNoProvForm ? (
                          <button
                            onClick={() => setShowCloseNoProvForm(true)}
                            className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl text-sm flex items-center justify-center gap-2 transition-colors">
                            <Ban size={14} className="text-gray-500" /> Close Ticket
                          </button>
                        ) : (
                          <div className="space-y-2 p-3 bg-red-50 rounded-xl border border-red-100">
                            <p className="text-[10px] font-black text-red-600 uppercase tracking-widest">Reason for closing</p>
                            <textarea
                              value={enquiryCloseComment}
                              onChange={e => setEnquiryCloseComment(e.target.value)}
                              rows={3}
                              placeholder="Enter reason why provisioning is not required…"
                              className="w-full text-xs text-gray-700 placeholder-gray-400 border border-red-200 rounded-lg p-2.5 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 resize-none bg-white"
                            />
                            <div className="flex gap-2">
                              <button onClick={handleCloseEnquiryNoProvisioning} disabled={closingEnquiry}
                                className="flex-1 py-2 text-white font-black rounded-lg text-xs flex items-center justify-center gap-1.5 disabled:opacity-60"
                                style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)' }}>
                                {closingEnquiry ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                                Close Docket
                              </button>
                              <button onClick={() => { setShowCloseNoProvForm(false); setEnquiryCloseComment('') }}
                                className="px-3 py-2 bg-white border border-gray-200 text-gray-600 font-bold rounded-lg text-xs">
                                Back
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  }

                  // Not feasible - just show close option
                  if (task.feasibilityStatus === 'not_feasible') {
                    return (
                      <div className="space-y-2">
                        <p className="text-[10px] font-black text-red-600 uppercase tracking-widest flex items-center gap-1.5">
                          <XCircle size={11} /> Site Not Feasible
                        </p>
                        {!showCloseNoProvForm ? (
                          <button
                            onClick={() => setShowCloseNoProvForm(true)}
                            className="w-full py-2.5 bg-red-50 hover:bg-red-100 text-red-600 font-bold rounded-xl text-sm flex items-center justify-center gap-2 transition-colors border border-red-200">
                            <Ban size={14} /> Close Docket
                          </button>
                        ) : (
                          <div className="space-y-2 p-3 bg-red-50 rounded-xl border border-red-100">
                            <p className="text-[10px] font-black text-red-600 uppercase tracking-widest">Reason for closing</p>
                            <textarea
                              value={enquiryCloseComment}
                              onChange={e => setEnquiryCloseComment(e.target.value)}
                              rows={3}
                              placeholder="Enter reason why provisioning is not required…"
                              className="w-full text-xs text-gray-700 placeholder-gray-400 border border-red-200 rounded-lg p-2.5 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 resize-none bg-white"
                            />
                            <div className="flex gap-2">
                              <button onClick={handleCloseEnquiryNoProvisioning} disabled={closingEnquiry}
                                className="flex-1 py-2 text-white font-black rounded-lg text-xs flex items-center justify-center gap-1.5 disabled:opacity-60"
                                style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)' }}>
                                {closingEnquiry ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                                Close Docket
                              </button>
                              <button onClick={() => { setShowCloseNoProvForm(false); setEnquiryCloseComment('') }}
                                className="px-3 py-2 bg-white border border-gray-200 text-gray-600 font-bold rounded-lg text-xs">
                                Back
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  }

                  // Fallback - awaiting feasibility
                  return (
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-100">
                      <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-amber-700 font-semibold leading-snug">
                        Awaiting feasibility study from the engineer.
                      </p>
                    </div>
                  )
                }

                // For open, assigned, in_progress: show standard move-to-next
                if (nextStatus && task.status !== 'pending_approval') {
                  const isChecklistCompleted = fieldsToVerify.every(f => tickedFields[f.key])
                  const isMarkingInProgress = nextStatus === 'in_progress'
                  const isDisabled = !!movingTo || (isMarkingInProgress && !isAdmin && !isChecklistCompleted)
                  return (
                    <div className="space-y-2">
                      <button onClick={() => handleStatusChange(nextStatus)} disabled={isDisabled}
                        className="w-full py-3 text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: isDisabled ? 'linear-gradient(135deg,#cbd5e1,#94a3b8)' : 'linear-gradient(135deg,#8b5cf6,#6366f1)', boxShadow: isDisabled ? 'none' : '0 4px 16px rgba(139,92,246,0.3)' }}>
                        {movingTo ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
                        Move to {STATUS_CFG[nextStatus].label}
                      </button>
                      {isMarkingInProgress && !isAdmin && !isChecklistCompleted && (
                        <p className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-100 rounded-lg p-2 flex items-center gap-1.5 leading-normal text-left">
                          <AlertTriangle size={11} className="shrink-0 text-amber-500" />
                          Complete the verification checklist to proceed.
                        </p>
                      )}
                    </div>
                  )
                }
                return null
              })()}

              {/* ── NON-ENQUIRY TYPE: standard flow ── */}
              {task.type !== 'enquiry' && !isDone && (
                <>
                  {isAdmin && task.status === 'pending_approval' && (
                    <button onClick={handleApprove} disabled={!!approvingId}
                      className="w-full py-3 text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.98] disabled:opacity-60"
                      style={{ background: 'linear-gradient(135deg,#10b981,#059669)', boxShadow: '0 4px 16px rgba(16,185,129,0.3)' }}>
                      {approvingId ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                      Approve & Send for Feedback
                    </button>
                  )}
                  {nextStatus && task.status !== 'pending_approval' && !isEnquiryInProgress && (() => {
                    const isChecklistCompleted = fieldsToVerify.every(f => tickedFields[f.key])
                    const isMarkingInProgress = nextStatus === 'in_progress'
                    const isDisabled = !!movingTo || (isMarkingInProgress && !isAdmin && !isChecklistCompleted)
                    return (
                      <div className="space-y-2">
                        <button onClick={() => handleStatusChange(nextStatus)} disabled={isDisabled}
                          className="w-full py-3 text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ background: isDisabled ? 'linear-gradient(135deg,#cbd5e1,#94a3b8)' : 'linear-gradient(135deg,#8b5cf6,#6366f1)', boxShadow: isDisabled ? 'none' : '0 4px 16px rgba(139,92,246,0.3)' }}>
                          {movingTo ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
                          Move to {STATUS_CFG[nextStatus].label}
                        </button>
                        {isMarkingInProgress && !isAdmin && !isChecklistCompleted && (
                          <p className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-100 rounded-lg p-2 flex items-center gap-1.5 leading-normal text-left">
                            <AlertTriangle size={11} className="shrink-0 text-amber-500" />
                            Complete the verification checklist above to start the task.
                          </p>
                        )}
                      </div>
                    )
                  })()}
                </>
              )}

              {/* Cancel */}
              {!isDone && isAdmin && (
                <button onClick={handleCancel} disabled={cancelling}
                  className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold rounded-xl text-sm flex items-center justify-center gap-2 transition-colors">
                  {cancelling ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                  {task.type === 'enquiry' ? 'Cancel Docket' : 'Cancel Task'}
                </button>
              )}
            </div>
          </div>

          {/* ── Task Details (sidebar meta) ── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-50 bg-gray-50/50">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Task Details</p>
            </div>
            <div className="px-4 py-2">
              <MetaRow label="Type" value={displayType} />
              <MetaRow label="Priority" value={p.label} />
              <MetaRow label="Created" value={task.createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} />
              {task.updatedAt && <MetaRow label="Updated" value={task.updatedAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} />}
              {task.closedAt && <MetaRow label="Closed" value={task.closedAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} />}
            </div>
          </div>

          {/* ── Assigned Engineer ── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-50 bg-gray-50/50">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Assignee</p>
            </div>
            <div className="p-4">
              {task.assignedEngineerName ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-xs font-black shrink-0"
                    style={{ background: 'linear-gradient(135deg,#2563eb,#06b6d4)', boxShadow: '0 4px 10px rgba(37,99,235,0.25)' }}>
                    {task.assignedEngineerName.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-800">{task.assignedEngineerName}</p>
                    <p className="text-[10px] text-blue-500 font-semibold">Field Engineer</p>
                  </div>
                  <UserCheck size={16} className="text-blue-400 ml-auto shrink-0" />
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">Unassigned</p>
              )}

              {canAssign && (
                <div className="mt-3 space-y-2">
                  <button onClick={() => setShowPicker(v => !v)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-blue-50 hover:bg-blue-100/70 border border-blue-200 rounded-xl text-sm font-bold text-blue-700 transition-colors">
                    <span className="flex items-center gap-2"><UserCheck size={13} /> {task.assignedEngineerName ? 'Reassign' : 'Assign Engineer'}</span>
                    <ChevronDown size={13} className={`transition-transform ${showPicker ? 'rotate-180' : ''}`} />
                  </button>
                  {showPicker && (
                    <div className="rounded-xl border border-gray-200 overflow-hidden shadow-md bg-white">
                      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 bg-gray-50">
                        <User size={12} className="text-gray-400" />
                        <input value={engSearch} onChange={e => setEngSearch(e.target.value)}
                          placeholder="Search engineer…" autoFocus
                          className="flex-1 text-sm outline-none text-gray-700 placeholder-gray-400 bg-transparent" />
                        {engSearch && <button onClick={() => setEngSearch('')}><X size={12} className="text-gray-400" /></button>}
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {filteredEngs.length === 0
                          ? <p className="py-5 text-center text-xs text-gray-400">No match</p>
                          : filteredEngs.map((eng, i) => (
                            <button key={eng.id} disabled={assigning} onClick={() => handleAssign(eng)}
                              className={`w-full flex items-center gap-3 px-3 py-3 hover:bg-blue-50 text-left transition-colors disabled:opacity-60 ${i > 0 ? 'border-t border-gray-50' : ''}`}>
                              <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-[10px] font-black shrink-0"
                                style={{ background: 'linear-gradient(135deg,#2563eb,#06b6d4)' }}>
                                {eng.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[13px] font-bold text-gray-800 truncate">{eng.name}</p>
                                <p className="text-[10px] text-gray-400 truncate">{eng.location}</p>
                              </div>
                              {assigning ? <Loader2 size={12} className="animate-spin text-blue-400 shrink-0" /> : <ArrowRight size={12} className="text-gray-300 shrink-0" />}
                            </button>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Share ── */}
          {isAdmin && task.assignedTo && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <button onClick={() => setShowShare(v => !v)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3.5 hover:bg-emerald-50/40 transition-colors">
                <div className="flex items-center gap-2">
                  <Share2 size={13} className="text-emerald-600" />
                  <p className="text-sm font-black text-gray-800">Share with Engineer</p>
                </div>
                <ChevronDown size={13} className={`text-gray-400 transition-transform ${showShare ? 'rotate-180' : ''}`} />
              </button>
              {showShare && (
                <div className="px-4 pb-4 space-y-3 border-t border-gray-100">
                  <div className="mt-3 bg-gray-50 rounded-xl px-3 py-2.5 max-h-24 overflow-y-auto">
                    <p className="text-[11px] text-gray-600 whitespace-pre-line leading-relaxed">{shareMessage}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={handleWhatsApp}
                      className="flex flex-col items-center gap-1.5 py-3 rounded-xl font-black text-[10px] text-white transition-all active:scale-95"
                      style={{ background: 'linear-gradient(135deg,#25d366,#128c7e)' }}>
                      <MessageSquare size={16} /> WhatsApp
                    </button>
                    <button onClick={handleSMS}
                      className="flex flex-col items-center gap-1.5 py-3 rounded-xl font-black text-[10px] text-white transition-all active:scale-95"
                      style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)' }}>
                      <Phone size={16} /> SMS
                    </button>
                    <button onClick={handleCopy}
                      className={`flex flex-col items-center gap-1.5 py-3 rounded-xl font-black text-[10px] transition-all active:scale-95 ${copied ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                      {copied ? <CheckCheck size={16} /> : <Copy size={16} />} {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TaskDetailPage
